import React, { useEffect, useMemo, useRef, useState } from "react";

declare global { interface Window { Calendly?: { initPopupWidget: (opts: { url: string }) => void; initInlineWidget: (opts: { url: string; parentElement: Element | null; prefill?: object }) => void; }; } }
import KYIntakeSystem from "./KYIntakeSystem";
import KYWeeklyCheckIn from "./KYWeeklyCheckIn";

type Lang = "en" | "es";
type BasicFormData = { fullName?: string; email?: string; phone?: string; goal?: string; trainingType?: string; };
type ReferralFormData = { fullName?: string; email?: string; phone?: string; goal?: string; };
type IntakeFormData = { fullName?: string; email?: string; phone?: string; goal?: string; format?: string; activity?: string; challenge?: string; readiness?: string; howHeard?: string; };
type SectionTitleProps = { label: string; title: string; subtitle?: string; };
type CardProps = { children: React.ReactNode; style?: React.CSSProperties; className?: string; };
type FieldProps = { label: string; children: React.ReactNode; };
type ResultItem = { img: string; title: string; sub: string; context: string; };
type Translations = typeof en;

const c = { gold: "#C9973A", goldLight: "#E8C06A", goldDark: "#8B6820", black: "#000000", bg: "#080806", card: "#0E0E0A", border: "#2A2410", text: "#F0E0C8", soft: "#CFC3A0", muted: "#8A7A50" };
const logo = "https://i.ibb.co/tpzz663d/KY-Empowered-Fitness-Logo-Clean-Version.png";
const couplePhoto = "/kevin-yuneisy-couple.jpg";
const kevinPhotos = ["/kevin-striking.jpg", "/kevin-overhead.jpg", "/kevin-bosu.jpg"];
const yuneisynPhotos = ["/yuneisy-hanging.jpg", "/yuneisy-chest-press.jpg", "/yuneisy-corrective.jpg"];

const en = {
  nav: { about: "About", story: "Our Story", results: "Results", services: "Services", contact: "Contact", apply: "Start Your Transformation" },
  hero: {
    eyebrow: "Miami Personal Training & Online Coaching Worldwide",
    headline: "Stop starting over. Build a stronger body and a lifestyle that finally lasts.",
    sub: "Kevin and Yuneisy run a structured system — training, nutrition, and real accountability built together — so you stop guessing and start making real progress.",
    cta: "Book Your Free Call", ctaSecondary: "See Results",
    ctaBook: "Book Your Free Call", ctaStart: "Start Your Transformation",
    scrollCue: "Or scroll to see real client results ↓",
    ctaMicrocopy: "Kevin and Yuneisy review every intake personally.",
    talkFirst: "Talk to us first.",
    bullets: ["Structured coaching for real life", "Accountability that keeps you consistent", "Training + nutrition in one system"],
    stats: [{ value: "14+", label: "Combined years of coaching" }, { value: "Miami", label: "In-person, home visit, online" }, { value: "Real", label: "Training + support + accountability" }],
  },
  results: {
    label: "Results & Proof", title: "Real people. Real transformations.",
    subtitle: "Commitment looks different on everyone. These are the results when it's met with the right coaching.",
    badge: "Client Result",
    exploreMore: "Explore More Transformations",
    showLess: "Show Less",
    items: [
      { img: "/Ana-1.JPG", title: "-44 lbs in 5 months", sub: "220 lbs to 176 lbs • stronger, leaner, more confident", context: "Working mom. Trained around her schedule." },
      { img: "/Josean1.jpg", title: "Visible muscle definition", sub: "Body recomposition • stronger upper body and back", context: "First time training consistently in years." },
      { img: "/Alex-M-1.jpg", title: "Lean, athletic progress", sub: "Sharper physique • stronger core and overall conditioning", context: "Started with zero gym experience." },
    ],
    moreItems: [
      { img: "/result-4.jpg", title: "Leaner. Stronger. More defined.", sub: "Body recomposition • significant fat loss and muscle gain", context: "Stayed consistent without changing his entire lifestyle." },
      { img: "/result-5.jpg", title: "Major weight loss transformation", sub: "Significant reduction in body weight • improved posture and confidence", context: "Showed up even on the hard days. The results followed." },
      { img: "/result-6.jpg", title: "Complete body transformation", sub: "Dramatic fat loss • visible muscle development throughout", context: "Committed to the process and never looked back." },
    ],
  },
  testimonials: {
    label: "Client Experience",
    title: "A coaching experience people remember",
    subtitle: "The details that matter most — how clients felt, what shifted, and what they'd tell someone on the fence.",
    transitionLine: "Here's what the experience actually feels like.",
    ctaBridge: "Every client above started with one conversation.",
    items: [
      { name: "Maria S.", quote: "I felt supported the entire time. It never felt extreme. It felt structured, personal, and sustainable." },
      { name: "Robert T.", quote: "At 68, I feel stronger, steadier, and more capable than I have in years. They truly coach the whole person." },
      { name: "Sury A.", quote: "Coach Yuny changed everything for me. I had tried several trainers with little results but she listened, had patience, and got it done. I dropped 40lbs, gained muscle strength, and improved my mental health. She is passionate about what she does and truly works with you." },
      { name: "Augusto M.", quote: "I started with doubts, insecurities, and a feeling of defeat. Kevin's coaching restored my confidence and health, and gave me the certainty that my investment of time and money was one of the best decisions I could have made. Don't hesitate — thank me later." },
      { name: "Melissa P.", quote: "Coach Yuny had me seeing results within the first few months. I felt stronger than ever and saw more muscle definition. She worked with me to help me reach my goals and helped me gain so much confidence." },
      { name: "Karen S.", quote: "I had an amazing experience training with Kevin and Yuny in Miami. They create fully customized workouts tailored to your goals. Their energy is always positive and motivating, and they genuinely care about your progress. I not only enjoyed the sessions but also saw real results." },
      { name: "Rohan R.", quote: "What truly makes Kevin stand apart is his contagious positive attitude. Working out with him is a workout for your mindset as well — you will leave pumped and ready to take on any challenges life has to offer. Best trainer I have personally ever worked with." },
    ],
  },
  googleReview: {
    rating: "5.0 on Google",
    quote: "Kevin and Yuny actually care about their clients' long term well being.",
    author: "Janelle Sales · Google Local Guide",
    cta: "View all reviews",
    url: "https://share.google/SohwqDrVbeYsYbYtg",
  },
  about: {
    label: "About KY", title: "Two coaches. One standard of excellence.",
    subtitle: "Two coaches. One standard. Built on the belief that intentional coaching changes more than just the body.",
    cards: [
      { title: "Built for real life", desc: "We help busy adults make progress with a plan they can actually follow, not a fantasy routine that disappears in two weeks." },
      { title: "Complementary coaching", desc: "Two perspectives, one system. Clients benefit from structure, detail, warmth, accountability, and a well-rounded coaching experience." },
      { title: "Human accountability", desc: "You do not just get workouts. You get communication, support, guidance, and coaching that keeps you moving forward." },
    ],
    readStory: "Read Our Story",
  },
  howWeCoach: {
    label: "In Action", title: "How We Coach",
    subtitle: "The difference is in the details. This is what intentional coaching looks like in practice.",
    anchorLine: "Real sessions. Real clients. This is what coaching with us looks like.",
    kevinName: "Coach Kevin", kevinTagline: "Structure · Intensity · Accountability",
    yuneisynName: "Coach Yuneisy", yuneisynTagline: "Precision · Energy · Transformation",
    brandLine: "Real coaching. Real attention. Real results.",
    cta: "Let's find out if we're the right fit.",
  },
  transformationSystem: {
    label: "The KY Transformation System",
    title: "This is not random training. It's a system.",
    subtitle: "Kevin and Yuneisy combine personalized programming, nutrition guidance, accountability check-ins, and adaptive coaching into one structured experience — so nothing falls through the cracks.",
    pillars: [
      { icon: "🎯", title: "Personalized Training", desc: "Custom sessions built around your body, movement, and goals." },
      { icon: "🥗", title: "Nutrition Guidance", desc: "Realistic nutrition support that fits your training and your life." },
      { icon: "📲", title: "Weekly Accountability", desc: "Regular check-ins that keep your momentum alive." },
      { icon: "📊", title: "Progress Tracking", desc: "What gets measured gets improved — we track what matters." },
      { icon: "📈", title: "Phase-Based Progression", desc: "Structured phases that build on each other toward lasting results." },
      { icon: "🔄", title: "Real-Life Adaptation", desc: "Your coaches adjust when life gets in the way." },
    ],
    closingLine: "Same system. Different delivery formats.",
    closingLineBold: "Kevin and Yuneisy help you choose the version that fits your life.",
    exploreBtn: "See Which Path Fits You",
  },
  howWeDeliver: {
    label: "The KY System", title: "One system. Three ways to experience it.",
    subtitle: "Every format delivers the same KY Transformation System — built around your goals, your life, and how you work best.",
    notSure: "Not sure which format fits you?",
    notSureBody: "Kevin and Yuneisy will help you decide during your first conversation. Every path begins with your intake — and your answers shape everything.",
    startBtn: "Start Your Transformation",
    paths: [
      { icon: "🏋️", title: "In-Person Coaching", tag: "Most Hands-On", highlight: true, desc: "Train directly with Kevin and Yuneisy in Miami. Real-time feedback, direct accountability, and coaching that adapts to you session by session. The most immersive version of the KY system.", fit: "Best for clients who want high accountability, real-time correction, and a fully guided experience." },
      { icon: "💻", title: "Online Coaching", tag: "Train From Anywhere", highlight: false, desc: "A structured coaching experience built around your lifestyle — wherever you are. Clear programming, nutrition guidance, check-ins, and direct communication with your coaches.", fit: "Best for self-motivated clients who travel, live outside Miami, or prefer to train on their own schedule." },
      { icon: "🔄", title: "Hybrid Coaching", tag: "Best of Both", highlight: false, desc: "Combines in-person sessions with online programming and support. Train with us when you can, stay structured when you can't. One unified system, two delivery modes.", fit: "Best for clients who want flexibility without losing accountability or structure." },
    ],
  },
  dualCta: {
    postResults: {
      eyebrow: "Start Here",
      headline: "Ready to stop guessing and build this the right way?",
      sub: "Kevin and Yuneisy will personally review your intake and guide you toward the coaching path that fits your life.",
      primary: "Start Your Transformation",
      secondary: "Talk to Us First",
    },
    postTestimonials: {
      eyebrow: "Start Here",
      headline: "Results like these start with one honest conversation.",
      sub: "Complete your intake and Kevin and Yuneisy will personally reach out to guide you forward.",
      primary: "Start Your Transformation",
      secondary: "Talk to Us First",
    },
  },
  startPage: {
    eyebrow: "KY Transformation System",
    headline: "This is where everything starts.",
    subhead: "Answer honestly. We'll take it from there.",
    body: "This isn't a generic questionnaire. This is how we build your plan — and how Kevin and Yuneisy get to know you before your first conversation.",
    beginBtn: "Let's Begin →",
    timeCopy: "About 3 minutes • No commitment required",
    stepOf: (s: number, t: number) => `Step ${s} of ${t}`,
    steps: {
      s1Label: "Step 1", s1Title: "Let's start with you.", s1Sub: "This is how Kevin and Yuneisy will reach out to you personally.",
      s2Label: "Step 2", s2Title: "What are you here to build?", s2Sub: "Be as honest as you want. There's no wrong answer here.",
      s3Label: "Step 3", s3Title: "Where are you right now?", s3Sub: "This helps Kevin and Yuneisy meet you exactly where you are — not where they assume you should be.",
      s4Label: "Step 4 — Before You Submit", s4Title: "Here's how KY delivers the system.", s4Sub: "There's one KY Transformation System — and three ways to experience it. Kevin and Yuneisy will help you find the right fit.",
    },
    fields: {
      name: "Full Name *", namePh: "Your full name",
      email: "Email Address *", emailPh: "you@email.com",
      phone: "Phone Number", phonePh: "+1 (305) 000-0000",
      goal: "Primary Goal *", goalPh: "Select your main goal...",
      format: "Preferred Coaching Format *", formatPh: "What feels right for you?",
      activity: "Current Activity Level *", activityPh: "Be honest with us...",
      challenge: "Biggest Challenge Right Now *", challengePh: "What's been in your way?",
      readiness: "How Ready Are You? *", readinessPh: "Where are you mentally?",
      howHeard: "How Did You Hear About KY?", howHeardPh: "Select one...",
    },
    activityLevels: ["Mostly sedentary — I need to start from scratch", "Lightly active — I move sometimes but inconsistently", "Moderately active — I work out but need more structure", "Very active — I want to level up my performance"],
    challenges: ["Staying consistent long-term", "Not knowing what to do or where to start", "Nutrition and diet habits", "Lack of time or accountability", "Recovering from injury or pain", "Motivation and mindset"],
    readinessOpts: ["I'm ready to start now", "I'm serious but have a few questions first", "I'm exploring my options"],
    howHeardOpts: ["Instagram", "Referred by a friend or client", "Google search", "NBC 6 Miami feature", "Other"],
    directionFooter: "You don't need to decide anything right now. Kevin and Yuneisy will review your intake and personally guide you toward the path that fits your goals, lifestyle, and situation.",
    back: "← Back", continue: "Continue →", submit: "Submit My Intake →", submitting: "Submitting...",
    successEyebrow: "You're In",
    successTitle: "Your transformation starts here.",
    successBody: "Kevin and Yuneisy will review your intake and reach out to you personally within 24 hours.",
    successNext: "What happens next",
    successSteps: ["Kevin & Yuneisy review your intake", "They reach out within 24 hours", "A short conversation to align on your goals", "Your coaching path is recommended — In-Person, Online, or Hybrid"],
    successInPersonBold: "Thinking about in-person training?",
    successInPersonBody: "For clients in Miami who want direct coaching and real-time accountability — in-person is where the most dramatic results happen. Kevin and Yuneisy will walk you through everything.",
    returnBtn: "← Return to KY",
    error: "Something went wrong. Please try again.",
  },
  creds: {
    label: "Credentials", title: "Certified. Experienced. Committed.",
    subtitle: "The credentials are there. So is the experience and intention behind every one of them.",
    intro: "You are not just working with enthusiastic trainers. You are working with professionals who hold the highest industry certifications, have led teams in real fitness operations, and have been recognized publicly for their expertise.",
    yearsExp: "years of coaching experience", certsLabel: "Certifications", specsLabel: "Specialties",
    bottomText: "Both Kevin and Yuneisy are NASM certified and continuously invest in their education to bring the highest standard of coaching to every client.",
    workWithUs: "Work With Us", asSeenOnLabel: "As Seen On", asSeenOnText: "Featured on NBC 6 Miami News", prevRoleLabel: "Previous Role",
    certList: [
      { abbr: "CPT", full: "Certified Personal Trainer", org: "NASM", plain: "The gold standard in personal training — certified to design safe, effective programs for any goal." },
      { abbr: "CES", full: "Corrective Exercise Specialist", org: "NASM", plain: "Trained to identify and correct movement imbalances — ideal for clients recovering from injury or pain." },
      { abbr: "PBC", full: "Physique & Bodybuilding Coach", org: "NASM", plain: "Certified to coach physique development and body composition." },
      { abbr: "CNC", full: "Certified Nutrition Coach", org: "NASM", plain: "Certified to provide personalized nutrition guidance that supports training and long-term lifestyle change." },
      { abbr: "Cupping", full: "Cupping Therapy", org: "Certified", plain: "A recovery technique that improves circulation, reduces muscle soreness, and accelerates healing." },
      { abbr: "Hyperice", full: "Hyperice Recovery", org: "Certified", plain: "Certified in advanced recovery technology to reduce soreness and keep clients performing at their best." },
      { abbr: "Stretching", full: "Assisted Stretching", org: "Certified", plain: "Certified in guided stretching techniques that improve flexibility, posture, and mobility over time." },
    ],
    coaches: [
      { name: "Kevin Fajardo", role: "Coach & Co-Founder", years: "8+", prevRole: "MMA Manager", nbc6: true, specs: ["Strength & Conditioning", "Boxing, Kickboxing & Muay Thai", "Weight Loss", "Corrective Exercise", "Nutrition Coaching"], certList: ["CPT", "CES", "PBC", "CNC", "Hyperice", "Stretching"] },
      { name: "Yuneisy Sarante", role: "Coach & Co-Founder", years: "6", prevRole: "Fitness Director", nbc6: true, specs: ["Weight Loss", "Strength & Conditioning", "Corrective Exercise", "Nutrition Coaching", "General Fitness"], certList: ["CPT", "CES", "PBC", "CNC", "Cupping", "Hyperice", "Stretching"] },
    ],
  },
  services: {
    label: "Services", title: "Coaching built around your goals",
    subtitle: "Every service is structured around you — your schedule, your body, and the level of support that keeps you consistent.",
    microHeader: "How we personalize your transformation",
    ask: "Ask about this service",
    items: [
      { icon: "🏋️", title: "Personal Training", desc: "Strength and conditioning built around your body and goals." },
      { icon: "🥊", title: "Boxing & Striking Conditioning", desc: "Skill-based sessions for cardio, coordination, and confidence." },
      { icon: "🥗", title: "Nutrition Guidance", desc: "Realistic nutrition coaching that supports consistency." },
      { icon: "🛠️", title: "Corrective & Recovery Support", desc: "Smart support for movement limitations and recovery." },
      { icon: "🚗", title: "Home Visits & Mobile Coaching", desc: "Train with us at your home or preferred location for convenience and accountability." },
      { icon: "💻", title: "Online Coaching", desc: "Full coaching and support from anywhere in the world." },
    ],
  },
  whoFor: {
    label: "Is KY Right For You?", title: "Built for people who are serious about change.",
    subtitle: "This isn't for everyone — and that's intentional. KY is built for people who are ready to be coached properly and expect results that reflect it.",
    introLine: "If you see yourself in any of these, you're in the right place.",
    items: [
      { icon: "❆", title: "You want real results, not quick fixes", desc: "You are ready to commit to a process that is built to last." },
      { icon: "❆", title: "You want a coach who actually knows you", desc: "You are tired of generic programs. You want someone who understands your body, your schedule, and your goals." },
      { icon: "❆", title: "You want to feel supported, not just instructed", desc: "Accountability, communication, and consistency are just as important to you as the workouts themselves." },
      { icon: "❆", title: "You are ready to start — online or in Miami", desc: "Whether you are in Miami or anywhere in the world, KY coaching meets you where you are." },
    ],
    cta: "Start Your Transformation", ctaMicrocopy: "Built around you, your goals, and your lifestyle.",
  },
  midCta: {
    eyebrow: "Start Here", title: "Let's Build Your Plan.",
    sub: "You don't need to have everything figured out. You just need a starting point.",
    body: "We'll walk through your goals, your schedule, and exactly what working together would look like.",
    cta: "Start Your Transformation", ctaMicrocopy: "No pressure. No commitment. Just clarity.", urgencyNote: "Miami in-person • Home visits • Online worldwide",
  },
  identityLine: "Miami-based. Real coaching. Real results.",
  investment: {
    label: "Investment",
    title: "The right level of coaching changes everything.",
    subtitle: "Every client enters the same KY Transformation System. The level of support, delivery, and access is tailored to your goals, lifestyle, and commitment level.",
    topLine: "One system. Three ways to step into it.",
    intro: "One KY system. Three ways in. Kevin and Yuneisy will help you choose the path that fits your life.",
    microcopyPre: "Start with clarity. We'll guide you from there.",
    cards: [
      { icon: "💻", title: "Online Coaching", badge: "Most Flexible", highlights: ["Custom programming", "Nutrition guidance", "Weekly check-ins", "Direct coach access"], desc: "A fully structured coaching experience from anywhere in the world — built around your life and delivered through clear programming, nutrition support, and direct communication with your coaches." },
      { icon: "🔀", title: "Hybrid Coaching", badge: "Best of Both", highlights: ["In-person sessions", "Online programming", "Flexible scheduling", "Dual-format support"], desc: "Combines in-person sessions with online programming and support. Train with Kevin and Yuneisy when you can, and stay structured when you can't." },
      { icon: "🏆", title: "In-Person Coaching", badge: "Maximum Accountability", highlights: ["Live form correction", "In-gym or home visits", "Highest-touch support", "Full system access"], desc: "The most immersive KY experience. Real-time feedback, direct accountability, and coaching that adapts to you session by session — in Miami or at your location." },
    ],
    qualifier: "Every path is built on the same KY system. The difference is how you access it.",
    closingLine: "Your intake helps Kevin and Yuneisy understand your goals, lifestyle, and starting point so they can guide you toward the right coaching path.",
    cta: "Start Your Transformation", ctaMicrocopy: "We'll guide you to the right path.",
  },
  referral: {
    eyebrow: "Referral Program", preheader: "Already part of the KY family?",
    title: "Know someone ready to transform? 💪",
    sub: "If you know someone who would thrive with KY Empowered Fitness, send them our way. Each approved referral currently earns one free session.",
    cta: "🎁 Refer a Friend", learnMore: "Learn More",
  },
  testimonialsPage: {
    label: "Client Stories", title: "Share Your KY Experience",
    subtitle: "Your story matters. If KY Empowered Fitness has made a difference in your life, we would love to hear from you.",
    note: "All submissions are reviewed personally by Kevin and Yuneisy before being shared publicly.",
    shareBtn: "⭐ Share Your Experience",
    fields: {
      name: "Full Name *", namePh: "Your name", coachingType: "Type of Coaching *", coachingTypePh: "Select...",
      duration: "How long have you been training with KY? *", durationPh: "Select...", rating: "Your Rating *",
      review: "Your Review *", reviewPh: "Tell us about your experience — what changed, what you felt, what surprised you...",
      photoUrl: "Photo Link (Optional)", photoUrlPh: "Instagram post link or photo URL",
      photoNote: "Share a link to a training photo or Instagram post — we may feature it alongside your review.",
      photoUpload: "Upload a Photo (Optional)", photoUploadNote: "Optional: upload a headshot or before/after photo with your review.",
      permission: "I give KY Empowered Fitness permission to display my review on their website and social media.",
    },
    durations: ["Less than 1 month", "1–3 months", "3–6 months", "6–12 months", "1 year or more"],
    coachingTypes: ["In-person", "Online", "Home visit", "Multiple types"],
    ratingLabels: ["", "Poor", "Fair", "Good", "Great", "Outstanding ❆"],
    submit: "Submit Your Review", submitting: "Submitting...",
    permissionRequired: "Please confirm your permission to proceed.",
    error: "Something went wrong. Please try again.",
    successTitle: "Thank You! 🙏",
    successBody: "Kevin and Yuneisy will personally review your submission. If selected, your story will be featured on the KY Empowered Fitness website.",
    successNote: "We truly appreciate you taking the time to share your experience.",
    backToSite: "Back to Site", modalTitle: "Already a KY client?",
    modalSub: "Your experience could inspire someone else's transformation.", modalCta: "⭐ Share Your Story",
  },
  contact: {
    label: "Contact", title: "Let's talk about your next chapter",
    subtitle: "Based in Miami, coaching clients in person, through home visits, and online.",
    apply: "Start Your Transformation",
    dmLabel: "Prefer to chat first?",
    dmText: "Send us a DM on Instagram",
    dmSub: "We respond personally to every message.",
    phoneLabel: "Or give us a call",
    phoneNumber: "+1 (786) 671-4965",
    phoneSub: "Available by call or text",
    googleLabel: "Find us on Google",
    googleSub: "Read reviews & get directions",
    googleUrl: "https://share.google/SohwqDrVbeYsYbYtg",
  },
  booking: {
    label: "Free Strategy Call",
    title: "Book Your Free Strategy Call",
    sub: "This is where we map out your next 90 days. No pressure. Just clarity, structure, and a plan built for you.",
    cta: "Schedule My Call",
    trust: "Spots are limited each week to ensure quality coaching.",
    bookPageHeadline: "Let's Build Your Transformation Plan",
    bookPageSub: "In 30 minutes, we'll break down your goals, your current situation, and exactly what it would take to get you results.",
    bookPageBack: "← Back to Home",
    postIntakeTitle: "You're In. Let's Lock This In.",
    postIntakeSub: "You took the first step. Now choose your time so we can build your plan together.",
    postIntakeCta: "Book My Call",
  },
  footer: { copy: "© 2026 KY Empowered Fitness • Miami, Florida" },
  stickyBtn: "❆ Start Your Transformation",
  trustBar: { label: "As Featured On", nbc: "NBC 6 Miami News", desc: "Kevin & Yuneisy — recognized fitness experts featured on NBC 6 Miami for their impact on the local fitness community." },
  goals: ["Build strength", "Improve body composition", "Move better and feel better", "Boost energy and health", "Need accountability"],
  coachingTypes: ["In-person", "Online", "Hybrid", "Home visit", "Not sure yet"],
  modal: {
    eyebrow: "Ask a Question", title: "Have a question? We'll answer it.",
    sub: "Ask anything about the coaching, the process, or whether KY is right for you. Kevin and Yuneisy will respond personally.",
    location: "Miami in-person • home visits • online coaching",
    submit: "Send My Question", sending: "Sending...",
    successEyebrow: "Question Sent", successTitle: "We'll get back to you.",
    successBody: "Kevin and Yuneisy will personally respond to your question as soon as possible.",
    backToSite: "Back to Site", error: "Something went wrong while sending your message. Please try again.",
    fName: "Full Name", fEmail: "Email Address", fPhone: "Phone Number", fGoal: "What are you looking for?", fStyle: "Preferred Coaching Format",
    phName: "Your name", phEmail: "you@email.com", phPhone: "+1 (305) 000-0000", phGoal: "Select...", phStyle: "Select...",
  },
  story: {
    label: "Our Story", title: "The story behind Kevin, Yuny, and KY Empowered Fitness",
    subtitle: "A coaching brand built on partnership, purpose, and the belief that real transformation should strengthen both body and mindset.",
    coachLabel: "Coaches Kevin & Yuneisy", heroTitle: "A couple building something bigger than fitness alone.",
    heroBody: "KY Empowered Fitness reflects who Kevin and Yuny are together: disciplined, caring, driven, and committed to helping people become stronger in every sense of the word.",
    sections: [
      { title: "How KY began", body: "KY Empowered Fitness was built from a shared belief that transformation should feel personal, intelligent, and sustainable. Two individual coaching journeys evolved into one united standard." },
      { title: "Why Kevin and Yuny work so well together", body: "Kevin brings structure, performance-minded coaching, and a strong eye for progression. Yuny brings warmth, attentiveness, and corrective support. Together they create an experience that feels both elevated and human." },
      { title: "What KY stands for", body: "Helping people build strength, confidence, resilience, and momentum in real life — from the first conversation to the lasting result." },
    ],
    back: "Back to Home",
  },
  talkPage: {
    eyebrow: "Start the Conversation",
    headline: "Not ready to commit? That's fine.",
    subhead: "Tell us what's on your mind.",
    body: "Kevin and Yuneisy read every message personally. Ask anything — about the coaching, the process, or whether KY is the right fit for where you are right now.",
    timeCopy: "Quick and low-pressure • No commitment required",
    fields: {
      name: "Full Name *", namePh: "Your name",
      email: "Email Address *", emailPh: "you@email.com",
      phone: "Phone Number", phonePh: "+1 (305) 000-0000",
      message: "What's on your mind? *", messagePh: "Ask anything — about the coaching, the process, whether KY is right for you, or what getting started actually looks like...",
      contactMethod: "Best Way to Reach You", contactMethodPh: "Select one...",
    },
    contactMethods: ["Email", "Phone or text", "Instagram DM"],
    submit: "Send My Message →",
    submitting: "Sending...",
    back: "← Back",
    returnBtn: "← Return to KY",
    readyStart: "Ready to go deeper?",
    readyStartLink: "Start your transformation intake instead.",
    successEyebrow: "Message Sent",
    successTitle: "We'll reach out soon.",
    successBody: "Kevin and Yuneisy will personally respond to your message — usually within 24 hours.",
    successNote: "If you're ready to go deeper at any point, you can always start your intake from the homepage.",
    intakePrompt: "Already ready to get started?",
    intakeLink: "Complete your full intake →",
    backToSite: "← Return to KY",
    error: "Something went wrong. Please try again.",
  },
  rp: {
    label: "KY Empowered Fitness", title: "Refer a Friend",
    subtitle: "Know someone ready to transform? Send them our way and earn one free session per approved referral.",
    rewardLabel: "Referral Reward", rewardValue: "1 Free Session 🎁",
    rewardNote: "Each approved referral currently earns one free session.",
    shareTitle: "📤 Share Your Code", shareBody: "Already a KY client? Message us on Instagram to get your personal referral code and start earning rewards.",
    getCode: "Get your code",
    sampleQuote: "I've been training with Kevin and Yuneisy at KY Empowered Fitness in Miami — use my referral code [YOUR CODE] when you apply!",
    newTitle: "🆕 New Client? Apply Here", newBody: "Were you referred by a KY client? Enter their referral code below so your submission can be linked correctly.",
    fName: "Full Name *", fEmail: "Email Address *", fPhone: "Phone Number", fGoal: "Primary Goal", fCode: "Referral Code (Optional)",
    phName: "Your full name", phEmail: "you@email.com", phPhone: "+1 (305) 000-0000", phGoal: "Select...", phCode: "e.g. MARIA2026",
    codeValid: "✓ Referred by", codeInvalid: "⚠ Code not recognized. Contact us on Instagram to verify.",
    submit: "🎁 Submit Referral", submitting: "Submitting...", error: "⚠ Something went wrong. Please try again.",
    successTitle: "You're In!", successWelcome: "Welcome", successRefBy: "You were referred by",
    successReward: "The referral reward tied to your submission is one free session.",
    successNote: "Kevin and Yuneisy will review your info and reach out within 24 hours.", backToSite: "← Back to Site",
  },
};

const es: Translations = {
  nav: { about: "Nosotros", story: "Nuestra Historia", results: "Resultados", services: "Servicios", contact: "Contacto", apply: "Comienza Tu Transformación" },
  hero: {
    eyebrow: "Entrenamiento Personal en Miami y Coaching Online en Todo el Mundo",
    headline: "Deja de empezar de nuevo. Construye un cuerpo más fuerte y un estilo de vida que por fin se mantiene.",
    sub: "Kevin y Yuneisy tienen un sistema estructurado — entrenamiento, nutrición y responsabilidad real juntos — para que dejes de adivinar y empieces a progresar de verdad.",
    cta: "Reserva Tu Llamada Gratuita", ctaSecondary: "Ver Resultados",
    ctaBook: "Reserva Tu Llamada Gratuita", ctaStart: "Comienza Tu Transformación",
    scrollCue: "O deslízate para ver resultados reales ↓",
    ctaMicrocopy: "Kevin y Yuneisy revisan cada intake personalmente.",
    talkFirst: "Habla con nosotros primero.",
    bullets: ["Coaching estructurado para tu vida real", "Responsabilidad que te mantiene constante", "Entrenamiento + nutrición en un sistema"],
    stats: [{ value: "14+", label: "Años combinados de coaching" }, { value: "Miami", label: "En persona, domicilio, online" }, { value: "Real", label: "Entrenamiento + apoyo + responsabilidad" }],
  },
  results: {
    label: "Resultados y Pruebas", title: "Personas reales. Transformaciones reales.",
    subtitle: "El compromiso se ve diferente en cada persona. Estos son los resultados cuando se combina con el coaching correcto.",
    badge: "Resultado Real",
    exploreMore: "Ver Más Transformaciones",
    showLess: "Ver Menos",
    items: [
      { img: "/Ana-1.JPG", title: "-44 lbs en 5 meses", sub: "220 lbs a 176 lbs • más fuerte, definida y segura", context: "Mamá trabajadora. Entrenó alrededor de su horario." },
      { img: "/Josean1.jpg", title: "Definición muscular visible", sub: "Recomposición corporal • parte superior e espalda más fuertes", context: "Primera vez entrenando con consistencia en años." },
      { img: "/Alex-M-1.jpg", title: "Progreso atlético y definido", sub: "Físico más marcado • core y acondicionamiento mejorados", context: "Comenzó sin experiencia previa en el gimnasio." },
    ],
    moreItems: [
      { img: "/result-4.jpg", title: "Más definido. Más fuerte. Más atlético.", sub: "Recomposición corporal • pérdida de grasa y ganancia muscular significativa", context: "Se mantuvo constante sin cambiar por completo su estilo de vida." },
      { img: "/result-5.jpg", title: "Transformación de pérdida de peso significativa", sub: "Reducción notable de peso corporal • mejor postura y confianza", context: "Se presentó incluso en los días difíciles. Los resultados llegaron." },
      { img: "/result-6.jpg", title: "Transformación corporal completa", sub: "Pérdida de grasa dramática • desarrollo muscular visible en todo el cuerpo", context: "Se comprometió con el proceso y nunca miró atrás." },
    ],
  },
  testimonials: {
    label: "Experiencia del Cliente",
    title: "Una experiencia de coaching que la gente recuerda",
    subtitle: "Los detalles que más importan — cómo se sintieron los clientes, qué cambió y qué le dirían a alguien indeciso.",
    transitionLine: "Así es como se siente la experiencia en realidad.",
    ctaBridge: "Cada cliente de arriba comenzó con una sola conversación.",
    items: [
      { name: "Maria S.", quote: "Me sentí apoyada en todo momento. Nunca se sintió extremo. Se sintió estructurado, personal y sostenible." },
      { name: "Robert T.", quote: "A los 68 años me siento más fuerte, estable y capaz que en años. Realmente entrenan a la persona completa." },
      { name: "Sury A.", quote: "Coach Yuny lo cambió todo para mí. Había probado varios entrenadores con pocos resultados pero ella escuchó, tuvo paciencia y lo logró. Bajé 40 libras, gané fuerza muscular y mejoré mi salud mental." },
      { name: "Augusto M.", quote: "Comencé con dudas, inseguridades y una sensación de derrota. El coaching de Kevin restauró mi confianza y mi salud, y me dio la certeza de que mi inversión de tiempo y dinero fue una de las mejores decisiones que pude haber tomado. No lo dudes — después me lo agradeces." },
      { name: "Melissa P.", quote: "Coach Yuny me hizo ver resultados en los primeros meses. Me sentí más fuerte que nunca y noté más definición muscular. Trabajó conmigo para ayudarme a alcanzar mis metas y me ayudó a ganar mucha confianza en mí misma." },
      { name: "Karen S.", quote: "Tuve una experiencia increíble entrenando con Kevin y Yuny en Miami. Crean entrenamientos completamente personalizados adaptados a tus metas. Su energía siempre es positiva y motivadora, y realmente se preocupan por tu progreso. No solo disfruté las sesiones sino que también vi resultados reales." },
      { name: "Rohan R.", quote: "Lo que realmente distingue a Kevin es su actitud positiva contagiosa. Entrenar con él también es un entrenamiento para tu mentalidad — saldrás lleno de energía y listo para enfrentar cualquier desafío. El mejor entrenador con el que he trabajado en mi vida." },
    ],
  },
  googleReview: {
    rating: "5.0 en Google",
    quote: "Kevin y Yuny realmente se preocupan por el bienestar a largo plazo de sus clientes.",
    author: "Janelle Sales · Google Local Guide",
    cta: "Ver todas las reseñas",
    url: "https://share.google/SohwqDrVbeYsYbYtg",
  },
  about: {
    label: "Sobre KY", title: "Dos coaches. Un estándar de excelencia.",
    subtitle: "Dos coaches. Un estándar. Construido sobre la creencia de que el coaching intencional cambia más que solo el cuerpo.",
    cards: [
      { title: "Diseñado para la vida real", desc: "Ayudamos a adultos ocupados a progresar con un plan que realmente pueden seguir, no una rutina que desaparece en dos semanas." },
      { title: "Coaching complementario", desc: "Dos perspectivas, un sistema. Los clientes se benefician de estructura, calidez, responsabilidad y una experiencia de coaching completa." },
      { title: "Responsabilidad humana", desc: "No solo obtienes entrenamientos. Obtienes comunicación, apoyo, orientación y coaching que te mantiene avanzando." },
    ],
    readStory: "Leer Nuestra Historia",
  },
  howWeCoach: {
    label: "En Acción", title: "Cómo Entrenamos",
    subtitle: "La diferencia está en los detalles. Así es como se ve el coaching intencional en la práctica.",
    anchorLine: "Sesiones reales. Clientes reales. Así es como se ve el coaching con nosotros.",
    kevinName: "Coach Kevin", kevinTagline: "Estructura · Intensidad · Responsabilidad",
    yuneisynName: "Coach Yuneisy", yuneisynTagline: "Precisión · Energía · Transformación",
    brandLine: "Coaching real. Atención real. Resultados reales.",
    cta: "Descubramos si somos la opción correcta.",
  },
  transformationSystem: {
    label: "El Sistema de Transformación KY",
    title: "Esto no es entrenamiento al azar. Es un sistema.",
    subtitle: "Kevin y Yuneisy combinan programación personalizada, guía nutricional, check-ins de responsabilidad y coaching adaptativo en una sola experiencia estructurada — para que nada quede al azar.",
    pillars: [
      { icon: "🎯", title: "Entrenamiento Personalizado", desc: "Sesiones diseñadas alrededor de tu cuerpo, movimiento y metas." },
      { icon: "🥗", title: "Guía de Nutrición", desc: "Nutrición realista que apoya tu entrenamiento sin complicar tu vida." },
      { icon: "📲", title: "Responsabilidad Semanal", desc: "Check-ins regulares que mantienen tu momentum activo." },
      { icon: "📊", title: "Seguimiento de Progreso", desc: "Lo que se mide, mejora — rastreamos lo que importa." },
      { icon: "📈", title: "Progresión por Fases", desc: "Fases estructuradas que se construyen hacia resultados duraderos." },
      { icon: "🔄", title: "Adaptación a la Vida Real", desc: "Tus coaches ajustan el plan cuando la vida se complica." },
    ],
    closingLine: "El mismo sistema. Diferentes formatos de entrega.",
    closingLineBold: "Kevin y Yuneisy te ayudan a elegir la versión que se adapta a tu vida.",
    exploreBtn: "Ver Qué Camino Te Conviene",
  },
  howWeDeliver: {
    label: "El Sistema KY", title: "Un sistema. Tres formas de vivirlo.",
    subtitle: "Cada formato entrega el mismo Sistema de Transformación KY — diseñado en torno a tus metas, tu vida y cómo trabajas mejor.",
    notSure: "¿No sabes cuál formato es el adecuado?",
    notSureBody: "Kevin y Yuneisy te ayudarán a decidir en tu primera conversación. Todo camino comienza con tu intake — y tus respuestas dan forma a todo.",
    startBtn: "Comienza Tu Transformación",
    paths: [
      { icon: "🏋️", title: "Coaching Presencial", tag: "Más Intensivo", highlight: true, desc: "Entrena directamente con Kevin y Yuneisy en Miami. Retroalimentación en tiempo real, responsabilidad directa y coaching que se adapta a ti sesión a sesión.", fit: "Ideal para clientes que quieren alta responsabilidad, corrección en tiempo real y una experiencia completamente guiada." },
      { icon: "💻", title: "Coaching Online", tag: "Entrena Desde Donde Estés", highlight: false, desc: "Una experiencia de coaching estructurada según tu estilo de vida — donde sea que estés. Programación clara, guía nutricional y comunicación directa con tus coaches.", fit: "Ideal para clientes automotivados que viajan o viven fuera de Miami." },
      { icon: "🔄", title: "Coaching Híbrido", tag: "Lo Mejor de Ambos", highlight: false, desc: "Combina sesiones presenciales con programación y apoyo online. Un sistema unificado, dos modalidades.", fit: "Ideal para clientes que quieren flexibilidad sin perder responsabilidad ni estructura." },
    ],
  },
  dualCta: {
    postResults: {
      eyebrow: "Empieza Aquí",
      headline: "¿Listo/a para dejar de adivinar y construir esto de la manera correcta?",
      sub: "Kevin y Yuneisy revisarán personalmente tu intake y te guiarán hacia el camino de coaching que se adapta a tu vida.",
      primary: "Comienza Tu Transformación",
      secondary: "Habla Con Nosotros Primero",
    },
    postTestimonials: {
      eyebrow: "Empieza Aquí",
      headline: "Resultados como estos comienzan con una conversación honesta.",
      sub: "Completa tu intake y Kevin y Yuneisy se comunicarán personalmente contigo para guiarte.",
      primary: "Comienza Tu Transformación",
      secondary: "Habla Con Nosotros Primero",
    },
  },
  startPage: {
    eyebrow: "Sistema de Transformación KY",
    headline: "Aquí es donde todo comienza.",
    subhead: "Responde con honestidad. Nosotros nos encargamos del resto.",
    body: "Esto no es un cuestionario genérico. Así es como construimos tu plan y cómo Kevin y Yuneisy te conocen antes de su primera conversación.",
    beginBtn: "Vamos a Comenzar →",
    timeCopy: "Unos 3 minutos • Sin compromiso",
    stepOf: (s: number, t: number) => `Paso ${s} of ${t}`,
    steps: {
      s1Label: "Paso 1", s1Title: "Hablemos de ti.", s1Sub: "Así es como Kevin y Yuneisy se comunicarán contigo personalmente.",
      s2Label: "Paso 2", s2Title: "¿Qué viniste a construir?", s2Sub: "Sé tan honesto/a como quieras. No hay respuesta incorrecta.",
      s3Label: "Paso 3", s3Title: "¿Dónde estás ahora?", s3Sub: "Esto ayuda a Kevin y Yuneisy a encontrarte exactamente donde estás.",
      s4Label: "Paso 4 — Antes de Enviar", s4Title: "Así es como KY entrega el sistema.", s4Sub: "Un Sistema de Transformación KY — tres formas de vivirlo. Kevin y Yuneisy te ayudarán a encontrar el correcto.",
    },
    fields: {
      name: "Nombre Completo *", namePh: "Tu nombre completo",
      email: "Correo Electrónico *", emailPh: "tu@correo.com",
      phone: "Número de Teléfono", phonePh: "+1 (305) 000-0000",
      goal: "Meta Principal *", goalPh: "Selecciona tu meta principal...",
      format: "Formato de Coaching Preferido *", formatPh: "¿Qué se siente bien para ti?",
      activity: "Nivel de Actividad Actual *", activityPh: "Sé honesto/a con nosotros...",
      challenge: "Mayor Desafío Ahora *", challengePh: "¿Qué ha estado en tu camino?",
      readiness: "¿Qué tan listo/a estás? *", readinessPh: "¿Dónde estás mentalmente?",
      howHeard: "¿Cómo conociste a KY?", howHeardPh: "Selecciona uno...",
    },
    activityLevels: ["Principalmente sedentario/a — necesito empezar desde cero", "Ligeramente activo/a — me muevo a veces pero sin consistencia", "Moderadamente activo/a — entreno pero necesito más estructura", "Muy activo/a — quiero llevar mi rendimiento al siguiente nivel"],
    challenges: ["Mantenerme consistente a largo plazo", "No saber qué hacer ni por dónde empezar", "Hábitos de nutrición y dieta", "Falta de tiempo o responsabilidad", "Recuperarse de una lesión o dolor", "Motivación y mentalidad"],
    readinessOpts: ["Estoy listo/a para comenzar ahora", "Estoy serio/a pero tengo algunas preguntas", "Estoy explorando mis opciones"],
    howHeardOpts: ["Instagram", "Referido/a por un amigo o cliente", "Búsqueda en Google", "Reportaje en NBC 6 Miami", "Otro"],
    directionFooter: "No necesitas decidir nada ahora. Kevin y Yuneisy revisarán tu intake y te guiarán personalmente hacia el camino que se adapta a tus metas, estilo de vida y situación.",
    back: "← Atrás", continue: "Continuar →", submit: "Enviar Mi Intake →", submitting: "Enviando...",
    successEyebrow: "Estás Adentro",
    successTitle: "Tu transformación comienza aquí.",
    successBody: "Kevin y Yuneisy revisarán tu intake y se comunicarán contigo personalmente en 24 horas.",
    successNext: "Qué pasa después",
    successSteps: ["Kevin y Yuneisy revisan tu intake", "Te contactan en 24 horas", "Una breve conversación para alinear tus metas", "Se recomienda tu camino de coaching — Presencial, Online o Híbrido"],
    successInPersonBold: "¿Pensando en entrenamiento presencial?",
    successInPersonBody: "Para clientes en Miami que quieren coaching directo y responsabilidad en tiempo real — el presencial es donde ocurren los resultados más impactantes. Kevin y Yuneisy te guiarán en todo.",
    returnBtn: "← Volver a KY",
    error: "Algo salió mal. Por favor intenta de nuevo.",
  },
  creds: {
    label: "Credenciales", title: "Certificados. Experimentados. Comprometidos.",
    subtitle: "Las certificaciones están ahí. También la experiencia y la intención detrás de cada una de ellas.",
    intro: "No estás trabajando solo con entrenadores entusiastas. Estás trabajando con profesionales que poseen las certificaciones más altas de la industria.",
    yearsExp: "años de experiencia como coaches", certsLabel: "Certificaciones", specsLabel: "Especialidades",
    bottomText: "Tanto Kevin como Yuneisy están certificados por NASM e invierten continuamente en su educación.",
    workWithUs: "Trabaja Con Nosotros", asSeenOnLabel: "Vistos En", asSeenOnText: "Destacados en NBC 6 Miami News", prevRoleLabel: "Rol Anterior",
    certList: [
      { abbr: "CPT", full: "Entrenador Personal Certificado", org: "NASM", plain: "El estándar de oro en entrenamiento personal." },
      { abbr: "CES", full: "Especialista en Ejercicio Correctivo", org: "NASM", plain: "Entrenado para identificar y corregir desequilibrios de movimiento." },
      { abbr: "PBC", full: "Coach de Físico y Culturismo", org: "NASM", plain: "Certificado para entrenar el desarrollo físico y la composición corporal." },
      { abbr: "CNC", full: "Coach de Nutrición Certificado", org: "NASM", plain: "Certificado para brindar orientación nutricional personalizada." },
      { abbr: "Cupping", full: "Terapia de Ventosas", org: "Certificado", plain: "Una técnica de recuperación que mejora la circulación y reduce el dolor muscular." },
      { abbr: "Hyperice", full: "Recuperación Hyperice", org: "Certificado", plain: "Certificado en tecnología avanzada de recuperación." },
      { abbr: "Stretching", full: "Estiramiento Asistido", org: "Certificado", plain: "Certificado en técnicas de estiramiento guiado que mejoran la flexibilidad." },
    ],
    coaches: [
      { name: "Kevin Fajardo", role: "Coach y Co-Fundador", years: "8+", prevRole: "MMA Manager", nbc6: true, specs: ["Fuerza y Acondicionamiento", "Boxeo, Kickboxing y Muay Thai", "Pérdida de Peso", "Ejercicio Correctivo", "Coaching de Nutrición"], certList: ["CPT", "CES", "PBC", "CNC", "Hyperice", "Stretching"] },
      { name: "Yuneisy Sarante", role: "Coach y Co-Fundadora", years: "6", prevRole: "Directora de Fitness", nbc6: true, specs: ["Pérdida de Peso", "Fuerza y Acondicionamiento", "Ejercicio Correctivo", "Coaching de Nutrición", "Fitness General"], certList: ["CPT", "CES", "PBC", "CNC", "Cupping", "Hyperice", "Stretching"] },
    ],
  },
  services: {
    label: "Servicios", title: "Coaching diseñado para tus metas",
    subtitle: "Cada servicio está estructurado en torno a ti — tu horario, tu cuerpo y el nivel de apoyo que te mantiene constante.",
    microHeader: "Cómo personalizamos tu transformación",
    ask: "Preguntar sobre este servicio",
    items: [
      { icon: "🏋️", title: "Entrenamiento Personal", desc: "Fuerza y acondicionamiento diseñados para tu cuerpo y metas." },
      { icon: "🥊", title: "Boxeo y Acondicionamiento de Striking", desc: "Sesiones atléticas para cardio, coordinación y confianza." },
      { icon: "🥗", title: "Guía de Nutrición", desc: "Coaching nutricional realista que apoya tu consistencia." },
      { icon: "🛠️", title: "Soporte Correctivo y de Recuperación", desc: "Apoyo inteligente para limitaciones de movimiento y recuperación." },
      { icon: "🚗", title: "Visitas a Domicilio y Coaching Móvil", desc: "Vamos donde estés — entrena donde más cómodo/a te sientas." },
      { icon: "💻", title: "Coaching Online", desc: "Coaching y apoyo completo desde cualquier parte del mundo." },
    ],
  },
  whoFor: {
    label: "¿Es KY Para Ti?", title: "Diseñado para personas serias sobre el cambio.",
    subtitle: "No es para todos — y eso es intencional. KY está diseñado para personas listas para ser entrenadas correctamente y que esperan resultados que lo reflejen.",
    introLine: "Si te identificas con alguno de estos puntos, estás en el lugar correcto.",
    items: [
      { icon: "❆", title: "Quieres resultados reales, no soluciones rápidas", desc: "Estás listo/a para comprometerte con un proceso duradero." },
      { icon: "❆", title: "Quieres un coach que realmente te conozca", desc: "Estás cansado/a de programas genéricos. Quieres alguien que entienda tu cuerpo y tus metas." },
      { icon: "❆", title: "Quieres sentirte apoyado/a, no solo instruido/a", desc: "La responsabilidad y la comunicación son tan importantes como los entrenamientos." },
      { icon: "❆", title: "Estás listo/a — online o en Miami", desc: "El coaching de KY se adapta a donde estás." },
    ],
    cta: "Comienza Tu Transformación", ctaMicrocopy: "Diseñado en torno a ti, tus metas y tu estilo de vida.",
  },
  midCta: {
    eyebrow: "Empieza Aquí", title: "Construyamos Tu Plan.",
    sub: "No necesitas tenerlo todo resuelto. Solo necesitas un punto de partida.",
    body: "Hablaremos sobre tus metas, tu horario y exactamente cómo sería trabajar juntos.",
    cta: "Comienza Tu Transformación", ctaMicrocopy: "Sin presión. Sin compromiso. Solo claridad.", urgencyNote: "Miami en persona • Visitas a domicilio • Online en todo el mundo",
  },
  identityLine: "Con base en Miami. Coaching real. Resultados reales.",
  investment: {
    label: "Inversión",
    title: "El nivel correcto de coaching lo cambia todo.",
    subtitle: "Cada cliente entra al mismo Sistema de Transformación KY. El nivel de apoyo, entrega y acceso se adapta a tus metas, estilo de vida y nivel de compromiso.",
    topLine: "Un sistema. Tres formas de vivirlo.",
    intro: "Un sistema KY. Tres formas de acceder. Kevin y Yuneisy te ayudarán a elegir el camino que se adapta a tu vida.",
    microcopyPre: "Empieza con claridad. Nosotros te guiamos desde ahí.",
    cards: [
      { icon: "💻", title: "Coaching Online", badge: "Más Flexible", highlights: ["Programación personalizada", "Guía nutricional", "Check-ins semanales", "Acceso directo a coaches"], desc: "Coaching KY completo desde donde estés. Programación, nutrición y acceso directo a tus coaches." },
      { icon: "🔀", title: "Coaching Híbrido", badge: "Lo Mejor de Ambos", highlights: ["Sesiones presenciales", "Programación online", "Horario flexible", "Apoyo en dos formatos"], desc: "Sesiones presenciales más estructura online. Entrena cuando puedas — sigue con coaching cuando no." },
      { icon: "🏆", title: "Coaching Presencial", badge: "Máxima Responsabilidad", highlights: ["Corrección de forma en vivo", "Gimnasio o visita a domicilio", "Apoyo más intensivo", "Acceso completo al sistema"], desc: "Coaching en tiempo real en Miami o tu ubicación. El mayor nivel de atención personal — cada sesión." },
    ],
    qualifier: "Cada camino está construido sobre el mismo sistema KY. La diferencia es cómo accedes a él.",
    closingLine: "Tu intake ayuda a Kevin y Yuneisy a entender tus metas, estilo de vida y punto de partida para guiarte hacia el camino de coaching correcto.",
    cta: "Comienza Tu Transformación", ctaMicrocopy: "Te guiaremos hacia el camino correcto.",
  },
  referral: {
    eyebrow: "Programa de Referidos", preheader: "¿Ya eres parte de la familia KY?",
    title: "¿Conoces a alguien listo para transformarse? 💪",
    sub: "Cada referido aprobado gana una sesión gratis.",
    cta: "🎁 Referir a un Amigo", learnMore: "Saber Más",
  },
  testimonialsPage: {
    label: "Historias de Clientes", title: "Comparte Tu Experiencia con KY",
    subtitle: "Tu historia importa. Si KY Empowered Fitness ha hecho una diferencia en tu vida, nos encantaría escucharte.",
    note: "Todas las solicitudes son revisadas personalmente por Kevin y Yuneisy antes de ser publicadas.",
    shareBtn: "⭐ Comparte Tu Experiencia",
    fields: {
      name: "Nombre Completo *", namePh: "Tu nombre", coachingType: "Tipo de Coaching *", coachingTypePh: "Seleccionar...",
      duration: "¿Cuánto tiempo llevas entrenando con KY? *", durationPh: "Seleccionar...", rating: "Tu Calificación *",
      review: "Tu Reseña *", reviewPh: "Cuéntanos sobre tu experiencia...",
      photoUrl: "Enlace de Foto (Opcional)", photoUrlPh: "Enlace de Instagram o URL de foto",
      photoNote: "Comparte un enlace a una foto de entrenamiento — podríamos incluirla junto a tu reseña.",
      photoUpload: "Subir una Foto (Opcional)", photoUploadNote: "Opcional: sube una foto tuya o de antes/después.",
      permission: "Doy permiso a KY Empowered Fitness para mostrar mi reseña en su sitio web y redes sociales.",
    },
    durations: ["Menos de 1 mes", "1–3 meses", "3–6 meses", "6–12 meses", "1 año o más"],
    coachingTypes: ["En persona", "Online", "Visita a domicilio", "Varios tipos"],
    ratingLabels: ["", "Regular", "Bien", "Muy bien", "Excelente", "Extraordinario ❆"],
    submit: "Enviar Mi Reseña", submitting: "Enviando...",
    permissionRequired: "Por favor confirma tu permiso para continuar.",
    error: "Algo salió mal. Por favor intenta de nuevo.",
    successTitle: "¡Gracias! 🙏",
    successBody: "Kevin y Yuneisy revisarán personalmente tu solicitud.",
    successNote: "Realmente apreciamos que te hayas tomado el tiempo de compartir tu experiencia.",
    backToSite: "Volver al Sitio", modalTitle: "¿Ya eres cliente de KY?",
    modalSub: "Tu experiencia podría inspirar la transformación de otra persona.", modalCta: "⭐ Comparte Tu Historia",
  },
  contact: {
    label: "Contacto", title: "Hablemos sobre tu próximo capítulo",
    subtitle: "Con base en Miami, entrenando clientes en persona, a domicilio y online.",
    apply: "Comienza Tu Transformación",
    dmLabel: "¿Prefieres charlar primero?",
    dmText: "Envíanos un DM en Instagram",
    dmSub: "Respondemos personalmente a cada mensaje.",
    phoneLabel: "O llámanos",
    phoneNumber: "+1 (786) 671-4965",
    phoneSub: "Disponible por llamada o mensaje de texto",
    googleLabel: "Encuéntranos en Google",
    googleSub: "Lee reseñas y obtén direcciones",
    googleUrl: "https://share.google/SohwqDrVbeYsYbYtg",
  },
  footer: { copy: "© 2026 KY Empowered Fitness • Miami, Florida" },
  stickyBtn: "❆ Comienza Tu Transformación",
  trustBar: { label: "Destacados En", nbc: "NBC 6 Miami News", desc: "Kevin y Yuneisy — expertos en fitness reconocidos, destacados en NBC 6 Miami." },
  goals: ["Construir fuerza", "Mejorar composición corporal", "Moverme mejor y sentirme mejor", "Aumentar energía y salud", "Necesito responsabilidad"],
  coachingTypes: ["En persona", "Online", "Híbrido", "Visita a domicilio", "Aún no estoy seguro/a"],
  modal: {
    eyebrow: "Haz una Pregunta", title: "¿Tienes una pregunta? Te respondemos.",
    sub: "Pregunta lo que quieras sobre el coaching, el proceso o si KY es para ti. Kevin y Yuneisy responden personalmente.",
    location: "En persona en Miami • visitas a domicilio • coaching online",
    submit: "Enviar Mi Pregunta", sending: "Enviando...",
    successEyebrow: "Pregunta Enviada", successTitle: "Te respondemos pronto.",
    successBody: "Kevin y Yuneisy responderán personalmente a tu pregunta lo antes posible.",
    backToSite: "Volver al Sitio", error: "Algo salió mal al enviar tu mensaje. Por favor intenta de nuevo.",
    fName: "Nombre Completo", fEmail: "Correo Electrónico", fPhone: "Número de Teléfono", fGoal: "¿Qué estás buscando?", fStyle: "Formato de Coaching Preferido",
    phName: "Tu nombre", phEmail: "tu@correo.com", phPhone: "+1 (305) 000-0000", phGoal: "Seleccionar...", phStyle: "Seleccionar...",
  },
  story: {
    label: "Nuestra Historia", title: "La historia detrás de Kevin, Yuny y KY Empowered Fitness",
    subtitle: "Una marca construida sobre asociación, propósito y la creencia de que la transformación real fortalece tanto el cuerpo como la mente.",
    coachLabel: "Coaches Kevin y Yuneisy", heroTitle: "Una pareja construyendo algo más grande que el fitness.",
    heroBody: "KY Empowered Fitness refleja quiénes son Kevin y Yuny juntos: disciplinados, cariñosos, motivados y comprometidos.",
    sections: [
      { title: "Cómo comenzó KY", body: "Dos caminos de coaching individuales evolucionaron hacia un estándar unificado de transformación personal, inteligente y sostenible." },
      { title: "Por qué Kevin y Yuny funcionan tan bien juntos", body: "Kevin aporta estructura y rendimiento. Yuny aporta calidez y apoyo correctivo. Juntos crean una experiencia elevada y humana." },
      { title: "Lo que representa KY", body: "Coaching premium que respeta el estilo de vida y el potencial de cada cliente — desde la primera conversación hasta el resultado duradero." },
    ],
    back: "Volver al Inicio",
  },
  talkPage: {
    eyebrow: "Comienza la Conversación",
    headline: "¿No estás listo/a para comprometerte? Está bien.",
    subhead: "Cuéntanos qué tienes en mente.",
    body: "Kevin y Yuneisy leen cada mensaje personalmente. Pregunta lo que quieras — sobre el coaching, el proceso o si KY es la opción correcta para donde estás ahora.",
    timeCopy: "Rápido y sin presión • Sin compromiso",
    fields: {
      name: "Nombre Completo *", namePh: "Tu nombre",
      email: "Correo Electrónico *", emailPh: "tu@correo.com",
      phone: "Número de Teléfono", phonePh: "+1 (305) 000-0000",
      message: "¿Qué tienes en mente? *", messagePh: "Pregunta lo que quieras — sobre el coaching, el proceso, si KY es para ti o cómo sería empezar...",
      contactMethod: "Mejor Forma de Contactarte", contactMethodPh: "Seleccionar...",
    },
    contactMethods: ["Correo electrónico", "Llamada o mensaje de texto", "DM en Instagram"],
    submit: "Enviar Mi Mensaje →",
    submitting: "Enviando...",
    back: "← Atrás",
    returnBtn: "← Volver a KY",
    readyStart: "¿Listo/a para ir más lejos?",
    readyStartLink: "Comienza tu intake de transformación.",
    successEyebrow: "Mensaje Enviado",
    successTitle: "Nos comunicaremos pronto.",
    successBody: "Kevin y Yuneisy responderán personalmente a tu mensaje — generalmente en 24 horas.",
    successNote: "Cuando estés listo/a para ir más lejos, puedes comenzar tu intake desde la página principal.",
    intakePrompt: "¿Ya estás listo/a para comenzar?",
    intakeLink: "Completa tu intake completo →",
    backToSite: "← Volver a KY",
    error: "Algo salió mal. Por favor intenta de nuevo.",
  },
  rp: {
    label: "KY Empowered Fitness", title: "Referir a un Amigo",
    subtitle: "¿Conoces a alguien listo para transformar su vida? Envíanoslo y gana una sesión gratis.",
    rewardLabel: "Recompensa por Referido", rewardValue: "1 Sesión Gratis 🎁",
    rewardNote: "Cada referido aprobado actualmente gana una sesión gratis.",
    shareTitle: "📤 Comparte Tu Código", shareBody: "¿Ya eres cliente de KY? Escríbenos en Instagram para obtener tu código.",
    getCode: "Obtén tu código",
    sampleQuote: "Usa mi código [TU CÓDIGO] cuando apliques en KY Empowered Fitness!",
    newTitle: "🆕 ¿Nuevo Cliente? Aplica Aquí", newBody: "¿Te refirió un cliente de KY? Ingresa su código de referido para que tu solicitud quede vinculada.",
    fName: "Nombre Completo *", fEmail: "Correo Electrónico *", fPhone: "Número de Teléfono", fGoal: "Meta Principal", fCode: "Código de Referido (Opcional)",
    phName: "Tu nombre completo", phEmail: "tu@correo.com", phPhone: "+1 (305) 000-0000", phGoal: "Seleccionar...", phCode: "ej. MARIA2026",
    codeValid: "✓ Referido/a por", codeInvalid: "⚠ Código no reconocido. Contáctanos en Instagram para verificar.",
    submit: "🎁 Enviar Referido", submitting: "Enviando...", error: "⚠ Algo salió mal. Por favor intenta de nuevo.",
    successTitle: "¡Estás Adentro!", successWelcome: "¡Bienvenido/a", successRefBy: "Fuiste referido/a por",
    successReward: "La recompensa vinculada a tu solicitud es una sesión gratis.",
    successNote: "Kevin y Yuneisy te contactarán en las próximas 24 horas.", backToSite: "← Volver al Sitio",
  },
  booking: {
    label: "Llamada Estratégica Gratuita",
    title: "Reserva Tu Llamada Estratégica Gratuita",
    sub: "Aquí es donde trazamos tus próximos 90 días. Sin presión. Solo claridad, estructura y un plan hecho para ti.",
    cta: "Agendar Mi Llamada",
    trust: "Los lugares son limitados cada semana para asegurar un coaching de calidad.",
    bookPageHeadline: "Construyamos Tu Plan de Transformación",
    bookPageSub: "En 30 minutos, analizaremos tus metas, tu situación actual y exactamente lo que se necesita para que obtengas resultados.",
    bookPageBack: "← Volver al Inicio",
    postIntakeTitle: "Ya Estás Adentro. Confirmemos Esto.",
    postIntakeSub: "Diste el primer paso. Ahora elige tu horario para que construyamos tu plan juntos.",
    postIntakeCta: "Reservar Mi Llamada",
  },
};

const translations: Record<Lang, Translations> = { en, es };
const referralCodes: Record<string, string> = { MARIA2026: "Maria S.", ROBERT2026: "Robert T.", JADE2026: "Jade M.", DEREK2026: "Derek P.", KY2026: "General Referral" };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveImg(path: string): string {
  if (!path) return logo;
  if (path.startsWith("http")) return path;
  return path.startsWith("/") ? path : `/${path}`;
}

function useFadeIn(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } }, { threshold });
    observer.observe(el); return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function FadeIn({ children, delay = 0, style }: { children: React.ReactNode; delay?: number; style?: React.CSSProperties; }) {
  const { ref, visible } = useFadeIn();
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(22px)", transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

// ─── Shared UI Components ─────────────────────────────────────────────────────

function SectionTitle({ label, title, subtitle }: SectionTitleProps) {
  return (
    <div style={{ textAlign: "center", marginBottom: 40 }}>
      <div style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 12 }}>{label}</div>
      <h2 style={{ color: c.text, fontSize: "clamp(28px,5vw,44px)", lineHeight: 1.08, margin: 0 }}>{title}</h2>
      {subtitle && <p style={{ color: c.muted, maxWidth: 760, margin: "16px auto 0", lineHeight: 1.8, fontSize: 15 }}>{subtitle}</p>}
    </div>
  );
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "0 auto 40px", maxWidth: 300 }}>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${c.gold})` }} />
      <span style={{ color: c.gold }}>❆</span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${c.gold}, transparent)` }} />
    </div>
  );
}

function Card({ children, style, className }: CardProps) {
  return <div className={className} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: 24, height: "100%", ...style }}>{children}</div>;
}

function Field({ label, children }: FieldProps) {
  return (
    <div>
      <label style={{ display: "block", color: c.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginBottom: 7, fontWeight: 700 }}>{label}</label>
      {children}
    </div>
  );
}

function LangToggle({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div style={{ display: "flex", border: `1px solid ${c.goldDark}66`, borderRadius: 8, overflow: "hidden" }}>
      {(["en", "es"] as Lang[]).map((l) => (
        <button key={l} onClick={() => setLang(l)} style={{ padding: "7px 13px", border: "none", background: lang === l ? `linear-gradient(135deg, ${c.goldDark}, ${c.gold})` : "transparent", color: lang === l ? c.black : c.muted, fontWeight: lang === l ? 900 : 400, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

// ─── StartPage ────────────────────────────────────────────────────────────────

function StartPage({ onBack, t }: { onBack: () => void; t: Translations }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<IntakeFormData>({});
  const [status, setStatus] = useState<"idle" | "sending" | "error">("idle");
  const sp = t.startPage;

  const inputStyle: React.CSSProperties = { width: "100%", borderRadius: 8, background: "#0a0a08", color: c.text, border: `1px solid ${c.border}`, padding: "12px 14px", fontFamily: "Georgia, serif", fontSize: 14, outline: "none" };
  const selectStyle: React.CSSProperties = { ...inputStyle, cursor: "pointer" };
  const primaryBtn: React.CSSProperties = { background: `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})`, color: c.black, border: 0, borderRadius: 8, padding: "16px 32px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 12 };
  const ghostBtn: React.CSSProperties = { background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "14px 28px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 11 };

  const totalSteps = 4;
  const progressPct = step === 0 ? 0 : step >= 5 ? 100 : Math.round((step / totalSteps) * 100);

  const goNext = () => { setStep(s => s + 1); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goBack = () => { setStep(s => s - 1); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xdawlovn", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          type: "KY Transformation Intake",
          "Full Name": data.fullName || "", Email: data.email || "", Phone: data.phone || "",
          "Primary Goal": data.goal || "", "Preferred Coaching Format": data.format || "",
          "Activity Level": data.activity || "", "Biggest Challenge": data.challenge || "",
          Readiness: data.readiness || "", "How They Heard": data.howHeard || "",
          Source: "KY Website — /start intake", "Submission Date": new Date().toLocaleDateString(),
        }),
      });
      if (!res.ok) throw new Error();
      setStep(5);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { setStatus("error"); }
  };

  const SubNav = () => (
    <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(8,8,6,.96)", borderBottom: `1px solid ${c.border}`, backdropFilter: "blur(12px)" }}>
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 20px", minHeight: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 12, border: 0, background: "transparent", cursor: "pointer", color: c.text, fontFamily: "inherit", padding: 0 }}>
          <img src={logo} alt="KY" style={{ width: 40, height: 40, objectFit: "contain" }} />
          <span style={{ color: c.gold, fontSize: 11, letterSpacing: 3, textTransform: "uppercase" }}>KY Empowered Fitness</span>
        </button>
        <button onClick={onBack} style={{ border: `1px solid ${c.border}`, background: "transparent", color: c.muted, borderRadius: 8, padding: "8px 16px", fontFamily: "inherit", fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
          ← {t.story.back}
        </button>
      </div>
    </nav>
  );

  const ProgressBar = () => (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ color: c.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>{sp.stepOf(step, totalSteps)}</span>
        <span style={{ color: c.gold, fontSize: 11 }}>{progressPct}%</span>
      </div>
      <div style={{ height: 3, background: c.border, borderRadius: 999 }}>
        <div style={{ height: "100%", width: `${progressPct}%`, background: `linear-gradient(90deg,${c.goldDark},${c.gold})`, borderRadius: 999, transition: "width 0.5s ease" }} />
      </div>
    </div>
  );

  const NavButtons = ({ canProceed, onNext }: { canProceed: boolean; onNext: () => void }) => (
    <div style={{ display: "flex", gap: 14, justifyContent: "space-between", alignItems: "center", marginTop: 28, flexWrap: "wrap" }}>
      <button onClick={goBack} style={{ ...ghostBtn, padding: "12px 20px" }}>{sp.back}</button>
      <button onClick={() => { if (canProceed) onNext(); }} disabled={!canProceed} style={{ ...primaryBtn, opacity: canProceed ? 1 : 0.4, cursor: canProceed ? "pointer" : "not-allowed" }}>{sp.continue}</button>
    </div>
  );

  if (step === 0) return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      <style>{`@keyframes glowPulse{0%,100%{box-shadow:0 0 20px rgba(139,104,32,.35)}50%{box-shadow:0 0 38px rgba(201,151,58,.52)}}`}</style>
      <SubNav />
      <div style={{ minHeight: "85vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
        <div style={{ maxWidth: 640, width: "100%", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
            <div style={{ width: 80, height: 1, background: `linear-gradient(90deg,transparent,${c.gold})`, alignSelf: "center" }} />
            <span style={{ color: c.gold, margin: "0 16px", fontSize: 20 }}>❆</span>
            <div style={{ width: 80, height: 1, background: `linear-gradient(90deg,${c.gold},transparent)`, alignSelf: "center" }} />
          </div>
          <p style={{ color: c.gold, fontSize: 11, letterSpacing: 6, textTransform: "uppercase", marginBottom: 20 }}>{sp.eyebrow}</p>
          <h1 style={{ color: c.text, fontSize: "clamp(34px,6vw,60px)", lineHeight: 1.05, margin: "0 0 20px" }}>{sp.headline}</h1>
          <p style={{ color: c.soft, fontSize: 18, lineHeight: 1.8, marginBottom: 12 }}>{sp.subhead}</p>
          <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.75, maxWidth: 480, margin: "0 auto 36px" }}>{sp.body}</p>
          <button onClick={goNext} style={{ ...primaryBtn, animation: "glowPulse 3s ease-in-out infinite", fontSize: 13, padding: "18px 36px" }}>{sp.beginBtn}</button>
          <p style={{ color: c.muted, fontSize: 12, marginTop: 22, letterSpacing: 1 }}>{sp.timeCopy}</p>
        </div>
      </div>
    </div>
  );

  if (step === 5) return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      <SubNav />
      <div style={{ minHeight: "85vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
        <div style={{ maxWidth: 580, width: "100%", textAlign: "center" }}>
          <span style={{ color: c.gold, fontSize: 48 }}>❆</span>
          <p style={{ color: c.gold, fontSize: 11, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14, marginTop: 20 }}>{sp.successEyebrow}</p>
          <h1 style={{ color: c.text, fontSize: "clamp(32px,5vw,52px)", lineHeight: 1.05, margin: "0 0 18px" }}>{sp.successTitle}</h1>
          <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.85, marginBottom: 28 }}>{sp.successBody}</p>
          <Card style={{ marginBottom: 24, borderColor: `${c.goldDark}55`, padding: "20px 24px", textAlign: "left" }}>
            <p style={{ color: c.gold, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>{sp.successNext}</p>
            {sp.successSteps.map((line, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < sp.successSteps.length - 1 ? 10 : 0 }}>
                <span style={{ color: c.gold, marginTop: 2, fontSize: 12 }}>❆</span>
                <span style={{ color: c.soft, fontSize: 14, lineHeight: 1.7 }}>{line}</span>
              </div>
            ))}
          </Card>
          <div style={{ background: `${c.goldDark}15`, border: `1px solid ${c.goldDark}44`, borderRadius: 10, padding: "16px 20px", marginBottom: 28 }}>
            <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.75, margin: 0 }}>
              💡 <strong style={{ color: c.gold }}>{sp.successInPersonBold}</strong> {sp.successInPersonBody}
            </p>
          </div>
          <div style={{ marginBottom: 20, padding: "24px 20px", background: `linear-gradient(135deg, ${c.goldDark}18, ${c.goldDark}08)`, border: `1px solid ${c.gold}55`, borderRadius: 14, textAlign: "center" }}>
            <p style={{ color: c.gold, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>{t.booking.postIntakeTitle}</p>
            <p style={{ color: c.soft, fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>{t.booking.postIntakeSub}</p>
            <button
              onClick={() => { if (window.Calendly) { window.Calendly.initPopupWidget({ url: "https://calendly.com/kyempoweredfitness/30min" }); } else { window.open("https://calendly.com/kyempoweredfitness/30min", "_blank"); } }}
              style={{ background: `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})`, color: c.black, border: 0, borderRadius: 8, padding: "14px 28px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: 12, animation: "glowPulse 3s ease-in-out infinite" }}
            >
              {t.booking.postIntakeCta}
            </button>
          </div>
          <button onClick={onBack} style={{ ...ghostBtn }}>{sp.returnBtn}</button>
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    if (step === 1) {
      const ok = !!(data.fullName?.trim() && data.email?.trim());
      return (
        <>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", marginBottom: 10 }}>{sp.steps.s1Label}</p>
          <h2 style={{ color: c.text, fontSize: "clamp(26px,5vw,40px)", lineHeight: 1.1, margin: "0 0 8px" }}>{sp.steps.s1Title}</h2>
          <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.75, marginBottom: 28 }}>{sp.steps.s1Sub}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <Field label={sp.fields.name}><input type="text" style={inputStyle} placeholder={sp.fields.namePh} value={data.fullName || ""} onChange={(e) => setData({ ...data, fullName: e.target.value })} /></Field>
            <Field label={sp.fields.email}><input type="email" style={inputStyle} placeholder={sp.fields.emailPh} value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })} /></Field>
            <Field label={sp.fields.phone}><input type="tel" style={inputStyle} placeholder={sp.fields.phonePh} value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} /></Field>
          </div>
          <NavButtons canProceed={ok} onNext={goNext} />
        </>
      );
    }
    if (step === 2) {
      const ok = !!(data.goal && data.format);
      return (
        <>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", marginBottom: 10 }}>{sp.steps.s2Label}</p>
          <h2 style={{ color: c.text, fontSize: "clamp(26px,5vw,40px)", lineHeight: 1.1, margin: "0 0 8px" }}>{sp.steps.s2Title}</h2>
          <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.75, marginBottom: 28 }}>{sp.steps.s2Sub}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <Field label={sp.fields.goal}><select style={selectStyle} value={data.goal || ""} onChange={(e) => setData({ ...data, goal: e.target.value })}><option value="">{sp.fields.goalPh}</option>{t.goals.map((g) => <option key={g} value={g}>{g}</option>)}</select></Field>
            <Field label={sp.fields.format}><select style={selectStyle} value={data.format || ""} onChange={(e) => setData({ ...data, format: e.target.value })}><option value="">{sp.fields.formatPh}</option>{t.coachingTypes.map((ct) => <option key={ct} value={ct}>{ct}</option>)}</select></Field>
          </div>
          <NavButtons canProceed={ok} onNext={goNext} />
        </>
      );
    }
    if (step === 3) {
      const ok = !!(data.activity && data.challenge && data.readiness);
      return (
        <>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", marginBottom: 10 }}>{sp.steps.s3Label}</p>
          <h2 style={{ color: c.text, fontSize: "clamp(26px,5vw,40px)", lineHeight: 1.1, margin: "0 0 8px" }}>{sp.steps.s3Title}</h2>
          <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.75, marginBottom: 28 }}>{sp.steps.s3Sub}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <Field label={sp.fields.activity}><select style={selectStyle} value={data.activity || ""} onChange={(e) => setData({ ...data, activity: e.target.value })}><option value="">{sp.fields.activityPh}</option>{sp.activityLevels.map((a) => <option key={a} value={a}>{a}</option>)}</select></Field>
            <Field label={sp.fields.challenge}><select style={selectStyle} value={data.challenge || ""} onChange={(e) => setData({ ...data, challenge: e.target.value })}><option value="">{sp.fields.challengePh}</option>{sp.challenges.map((ch) => <option key={ch} value={ch}>{ch}</option>)}</select></Field>
            <Field label={sp.fields.readiness}><select style={selectStyle} value={data.readiness || ""} onChange={(e) => setData({ ...data, readiness: e.target.value })}><option value="">{sp.fields.readinessPh}</option>{sp.readinessOpts.map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
            <Field label={sp.fields.howHeard}><select style={selectStyle} value={data.howHeard || ""} onChange={(e) => setData({ ...data, howHeard: e.target.value })}><option value="">{sp.fields.howHeardPh}</option>{sp.howHeardOpts.map((h) => <option key={h} value={h}>{h}</option>)}</select></Field>
          </div>
          <NavButtons canProceed={ok} onNext={goNext} />
        </>
      );
    }
    if (step === 4) {
      const paths = t.howWeDeliver.paths;
      return (
        <>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", marginBottom: 10 }}>{sp.steps.s4Label}</p>
          <h2 style={{ color: c.text, fontSize: "clamp(24px,4.5vw,38px)", lineHeight: 1.1, margin: "0 0 10px" }}>{sp.steps.s4Title}</h2>
          <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.75, marginBottom: 28 }}>{sp.steps.s4Sub}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
            {paths.map((path) => (
              <div key={path.title} style={{ background: path.highlight ? "linear-gradient(160deg,#0f0e08,#14120a)" : c.card, border: `1px solid ${path.highlight ? c.gold : c.border}`, borderRadius: 14, padding: 22, position: "relative", boxShadow: path.highlight ? `0 0 32px ${c.goldDark}33` : "none" }}>
                {path.highlight && <div style={{ position: "absolute", top: -1, right: 18, background: `linear-gradient(135deg,${c.goldDark},${c.gold})`, color: c.black, fontSize: 9, letterSpacing: 2, fontWeight: 900, padding: "4px 12px", borderRadius: "0 0 8px 8px", textTransform: "uppercase" }}>{path.tag}</div>}
                <div style={{ fontSize: 26, marginBottom: 10, marginTop: path.highlight ? 10 : 0 }}>{path.icon}</div>
                <h3 style={{ color: path.highlight ? c.gold : c.text, fontSize: 17, margin: "0 0 8px" }}>{path.title}</h3>
                <p style={{ color: c.muted, fontSize: 12, lineHeight: 1.75, margin: "0 0 8px" }}>{path.desc}</p>
                <p style={{ color: c.soft, fontSize: 11, lineHeight: 1.65, fontStyle: "italic", margin: 0 }}>{path.fit}</p>
              </div>
            ))}
          </div>
          <div style={{ background: `${c.goldDark}12`, border: `1px solid ${c.goldDark}33`, borderRadius: 12, padding: "16px 20px", marginBottom: 24, textAlign: "center" }}>
            <p style={{ color: c.soft, fontSize: 13, lineHeight: 1.8, margin: 0 }}>{sp.directionFooter}</p>
          </div>
          {status === "error" && <div style={{ background: "#1a0505", border: "1px solid #8f2d2d", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}><p style={{ color: "#ef8d8d", fontSize: 13, margin: 0 }}>{sp.error}</p></div>}
          <div style={{ display: "flex", gap: 14, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={goBack} style={{ ...ghostBtn, padding: "12px 20px" }}>{sp.back}</button>
            <button onClick={submit} disabled={status === "sending"} style={{ ...primaryBtn, opacity: status === "sending" ? 0.5 : 1, cursor: status === "sending" ? "not-allowed" : "pointer", animation: "glowPulse 3s ease-in-out infinite" }}>{status === "sending" ? sp.submitting : sp.submit}</button>
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      <style>{`@keyframes glowPulse{0%,100%{box-shadow:0 0 20px rgba(139,104,32,.35)}50%{box-shadow:0 0 38px rgba(201,151,58,.52)}}`}</style>
      <SubNav />
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "50px 20px 80px" }}>
        <ProgressBar />
        {renderStep()}
      </div>
    </div>
  );
}

// ─── TalkPage ─────────────────────────────────────────────────────────────────

function TalkPage({ onBack, t, onStart, onIntake }: { onBack: () => void; t: Translations; onStart: () => void; onIntake: () => void }) {
  const [data, setData] = useState<{ name?: string; email?: string; phone?: string; message?: string; contactMethod?: string }>({});
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const tp = t.talkPage;

  const inputStyle: React.CSSProperties = { width: "100%", borderRadius: 8, background: "#0a0a08", color: c.text, border: `1px solid ${c.border}`, padding: "12px 14px", fontFamily: "Georgia, serif", fontSize: 14, outline: "none" };
  const canSubmit = !!(data.name?.trim() && data.email?.trim() && data.message?.trim() && data.message.trim().length > 5);

  const submit = async () => {
    if (!canSubmit) return;
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xdawlovn", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          type: "KY Talk — Soft Lead",
          "Full Name": data.name || "", Email: data.email || "", Phone: data.phone || "",
          "Message": data.message || "", "Preferred Contact Method": data.contactMethod || "Not specified",
          Source: "KY Website — /talk", "Submission Date": new Date().toLocaleDateString(),
        }),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { setStatus("error"); }
  };

  const SubNav = () => (
    <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(8,8,6,.96)", borderBottom: `1px solid ${c.border}`, backdropFilter: "blur(12px)" }}>
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 20px", minHeight: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 12, border: 0, background: "transparent", cursor: "pointer", color: c.text, fontFamily: "inherit", padding: 0 }}>
          <img src={logo} alt="KY" style={{ width: 40, height: 40, objectFit: "contain" }} />
          <span style={{ color: c.gold, fontSize: 11, letterSpacing: 3, textTransform: "uppercase" }}>KY Empowered Fitness</span>
        </button>
        <button onClick={onBack} style={{ border: `1px solid ${c.border}`, background: "transparent", color: c.muted, borderRadius: 8, padding: "8px 16px", fontFamily: "inherit", fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>{tp.back}</button>
      </div>
    </nav>
  );

  if (status === "success") return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      <SubNav />
      <div style={{ minHeight: "85vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
        <div style={{ maxWidth: 560, width: "100%", textAlign: "center" }}>
          <span style={{ color: c.gold, fontSize: 44 }}>❆</span>
          <p style={{ color: c.gold, fontSize: 11, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14, marginTop: 20 }}>{tp.successEyebrow}</p>
          <h1 style={{ color: c.text, fontSize: "clamp(30px,5vw,48px)", lineHeight: 1.05, margin: "0 0 16px" }}>{tp.successTitle}</h1>
          <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.85, marginBottom: 20 }}>{tp.successBody}</p>
          <div style={{ background: `${c.goldDark}12`, border: `1px solid ${c.goldDark}33`, borderRadius: 10, padding: "16px 20px", marginBottom: 28, textAlign: "left" }}>
            <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.75, margin: 0 }}>{tp.successNote}</p>
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={onStart} style={{ background: `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})`, color: c.black, border: 0, borderRadius: 8, padding: "14px 24px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 11 }}>{tp.readyStartLink}</button>
            <button onClick={onBack} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "14px 24px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 11 }}>{tp.backToSite}</button>
          </div>
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${c.border}`, textAlign: "center" }}>
            <p style={{ color: c.muted, fontSize: 13, marginBottom: 10, lineHeight: 1.6 }}>{tp.intakePrompt}</p>
            <button onClick={onIntake} style={{ background: "none", border: "none", color: c.gold, cursor: "pointer", fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit", padding: 0, letterSpacing: 0.5 }}>
              {tp.intakeLink}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      <SubNav />
      <div style={{ maxWidth: 580, margin: "0 auto", padding: "60px 20px 80px" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 16 }}>{tp.eyebrow}</p>
          <h1 style={{ color: c.text, fontSize: "clamp(30px,5vw,48px)", lineHeight: 1.05, margin: "0 0 14px" }}>{tp.headline}</h1>
          <p style={{ color: c.soft, fontSize: 17, lineHeight: 1.8, marginBottom: 10 }}>{tp.subhead}</p>
          <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.75, maxWidth: 440, margin: "0 auto 10px" }}>{tp.body}</p>
          <p style={{ color: c.muted, fontSize: 11, letterSpacing: 1, marginBottom: 0 }}>{tp.timeCopy}</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Field label={tp.fields.name}><input type="text" style={inputStyle} placeholder={tp.fields.namePh} value={data.name || ""} onChange={(e) => setData({ ...data, name: e.target.value })} /></Field>
          <Field label={tp.fields.email}><input type="email" style={inputStyle} placeholder={tp.fields.emailPh} value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })} /></Field>
          <Field label={tp.fields.phone}><input type="tel" style={inputStyle} placeholder={tp.fields.phonePh} value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} /></Field>
          <Field label={tp.fields.message}><textarea style={{ ...inputStyle, minHeight: 130, resize: "vertical", lineHeight: 1.7 }} placeholder={tp.fields.messagePh} value={data.message || ""} onChange={(e) => setData({ ...data, message: e.target.value })} /></Field>
          <Field label={tp.fields.contactMethod}><select style={{ ...inputStyle, cursor: "pointer" }} value={data.contactMethod || ""} onChange={(e) => setData({ ...data, contactMethod: e.target.value })}><option value="">{tp.fields.contactMethodPh}</option>{tp.contactMethods.map((m) => <option key={m} value={m}>{m}</option>)}</select></Field>
        </div>
        <div style={{ marginTop: 14, padding: "12px 16px", background: `${c.goldDark}10`, border: `1px solid ${c.goldDark}28`, borderRadius: 8, textAlign: "center" }}>
          <span style={{ color: c.muted, fontSize: 12 }}>{tp.readyStart} </span>
          <button onClick={onStart} style={{ background: "none", border: "none", color: c.gold, cursor: "pointer", fontSize: 12, textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit", padding: 0 }}>{tp.readyStartLink}</button>
        </div>
        {status === "error" && <div style={{ marginTop: 16, background: "#1a0505", border: "1px solid #8f2d2d", borderRadius: 8, padding: "12px 16px" }}><p style={{ color: "#ef8d8d", fontSize: 13, margin: 0 }}>{tp.error}</p></div>}
        <div style={{ display: "flex", gap: 14, justifyContent: "space-between", alignItems: "center", marginTop: 24, flexWrap: "wrap" }}>
          <button onClick={onBack} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "12px 20px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 11 }}>{tp.back}</button>
          <button onClick={submit} disabled={!canSubmit || status === "sending"} style={{ background: canSubmit && status !== "sending" ? `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})` : `${c.goldDark}55`, color: c.black, border: 0, borderRadius: 8, padding: "16px 28px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: canSubmit && status !== "sending" ? "pointer" : "not-allowed", fontFamily: "inherit", fontSize: 12 }}>
            {status === "sending" ? tp.submitting : tp.submit}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section Components ───────────────────────────────────────────────────────

function HowWeDeliverSection({ t, onStart }: { t: Translations; onStart: () => void }) {
  const hwd = t.howWeDeliver;
  return (
    <section id="how-we-deliver" style={{ maxWidth: 1120, margin: "0 auto", padding: "90px 20px" }}>
      <FadeIn><Divider /><SectionTitle label={hwd.label} title={hwd.title} subtitle={hwd.subtitle} /></FadeIn>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 22, marginBottom: 36 }}>
        {hwd.paths.map((path, i) => (
          <FadeIn key={path.title} delay={i * 100}>
            <div style={{ background: path.highlight ? "linear-gradient(160deg,#0f0e08,#14120a)" : c.card, border: `1px solid ${path.highlight ? c.gold : c.border}`, borderRadius: 14, padding: 28, position: "relative", boxShadow: path.highlight ? `0 0 40px ${c.goldDark}33` : "none", display: "flex", flexDirection: "column", height: "100%" }}>
              {path.highlight && <div style={{ position: "absolute", top: -1, right: 20, background: `linear-gradient(135deg,${c.goldDark},${c.gold})`, color: c.black, fontSize: 9, letterSpacing: 2, fontWeight: 900, padding: "5px 14px", borderRadius: "0 0 8px 8px", textTransform: "uppercase" }}>{path.tag}</div>}
              <div style={{ fontSize: 30, marginBottom: 14, marginTop: path.highlight ? 10 : 0 }}>{path.icon}</div>
              <h3 style={{ color: path.highlight ? c.gold : c.text, fontSize: 21, margin: "0 0 12px" }}>{path.title}</h3>
              <p style={{ color: c.muted, fontSize: 14, lineHeight: 1.85, margin: "0 0 14px", flex: 1 }}>{path.desc}</p>
              <div style={{ background: `${c.goldDark}18`, borderRadius: 8, padding: "10px 14px", marginBottom: 20 }}>
                <p style={{ color: c.soft, fontSize: 12, lineHeight: 1.7, margin: 0, fontStyle: "italic" }}>{path.fit}</p>
              </div>
              <button onClick={onStart}
                style={{ background: "transparent", border: `1px solid ${path.highlight ? c.goldDark : c.border}`, borderRadius: 8, color: path.highlight ? c.gold : c.muted, padding: "11px 16px", cursor: "pointer", fontFamily: "inherit", letterSpacing: 1, fontSize: 11, textTransform: "uppercase", transition: "border-color 0.2s, color 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = c.gold; e.currentTarget.style.color = c.goldLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = path.highlight ? c.goldDark : c.border; e.currentTarget.style.color = path.highlight ? c.gold : c.muted; }}>
                {hwd.startBtn}
              </button>
            </div>
          </FadeIn>
        ))}
      </div>
      <FadeIn>
        <div style={{ background: `${c.goldDark}10`, border: `1px solid ${c.goldDark}33`, borderRadius: 14, padding: "22px 28px", textAlign: "center" }}>
          <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.8, margin: 0 }}>
            <strong style={{ color: c.gold }}>{hwd.notSure}</strong>{" "}{hwd.notSureBody}
          </p>
        </div>
      </FadeIn>
    </section>
  );
}

function DualCTABlock({ eyebrow, headline, sub, primary, secondary, onPrimary, onSecondary }: {
  eyebrow: string; headline: string; sub: string; primary: string; secondary: string; onPrimary: () => void; onSecondary: () => void;
}) {
  return (
    <FadeIn>
      <div style={{ maxWidth: 1120, margin: "0 auto", background: "linear-gradient(135deg,rgba(12,12,7,1),rgba(20,18,10,1),rgba(12,12,7,1))", border: `1px solid ${c.goldDark}44`, borderRadius: 20, padding: "42px 28px", textAlign: "center" }}>
        <p style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 12 }}>{eyebrow}</p>
        <h2 style={{ color: c.text, fontSize: "clamp(26px,4.5vw,42px)", margin: "0 0 12px", lineHeight: 1.1 }}>{headline}</h2>
        <p style={{ color: c.soft, lineHeight: 1.8, maxWidth: 600, margin: "0 auto 24px", fontSize: 15 }}>{sub}</p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={onPrimary} style={{ background: `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})`, color: c.black, border: 0, borderRadius: 8, padding: "16px 28px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12, animation: "glowPulse 3s ease-in-out infinite" }}>{primary}</button>
          <button onClick={onSecondary} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "16px 28px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>{secondary}</button>
        </div>
      </div>
    </FadeIn>
  );
}

// ─── NEW: Transformation System Section (Upgrade 2) ───────────────────────────

function TransformationSystemSection({ t, onStart }: { t: Translations; onStart: () => void }) {
  const ts = t.transformationSystem;
  return (
    <section style={{ padding: "90px 20px", background: "linear-gradient(180deg, #080806, #0c0b07, #080806)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 0%, rgba(139,104,32,.08), transparent 60%)`, pointerEvents: "none" }} />
      <div style={{ maxWidth: 1120, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <FadeIn>
          <Divider />
          <SectionTitle label={ts.label} title={ts.title} subtitle={ts.subtitle} />
        </FadeIn>
        <div className="ky-system-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 48 }}>
          {ts.pillars.map((pillar, i) => (
            <FadeIn key={pillar.title} delay={i * 80}>
              <div
                className="ky-system-card" style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: "24px 22px", display: "flex", flexDirection: "column", gap: 12, transition: "border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${c.goldDark}88`; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 16px 40px rgba(0,0,0,0.3)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div className="ky-system-card-icon" style={{ width: 44, height: 44, borderRadius: 10, background: `linear-gradient(135deg, ${c.goldDark}22, ${c.goldDark}44)`, border: `1px solid ${c.goldDark}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                    {pillar.icon}
                  </div>
                  <h3 className="ky-system-card-title" style={{ color: c.text, fontSize: 17, margin: 0, fontWeight: 700, lineHeight: 1.3 }}>{pillar.title}</h3>
                </div>
                <p className="ky-system-card-desc" style={{ color: c.muted, fontSize: 14, lineHeight: 1.8, margin: 0 }}>{pillar.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn>
          <div style={{ background: "linear-gradient(135deg, rgba(12,12,7,1), rgba(20,18,10,1), rgba(12,12,7,1))", border: `1px solid ${c.goldDark}44`, borderRadius: 16, padding: "32px 28px", textAlign: "center", maxWidth: 760, margin: "0 auto" }}>
            <p style={{ color: c.goldLight, fontSize: 15, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 10px", fontStyle: "italic" }}>{ts.closingLine}</p>
            <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.8, margin: "0 0 24px" }}>{ts.closingLineBold}</p>
            <button
              onClick={onStart}
              style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "13px 28px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12, transition: "background 0.2s, color 0.2s" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${c.goldDark}22`; e.currentTarget.style.color = c.goldLight; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = c.gold; }}
            >
              {ts.exploreBtn}
            </button>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── ResultCard + ResultsSection ──────────────────────────────────────────────

function ResultCard({ item, badge }: { item: ResultItem; badge: string }) {
  return (
    <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${c.border}`, background: c.card, transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 18px 40px rgba(0,0,0,0.28)"; e.currentTarget.style.borderColor = c.goldDark; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = c.border; }}>
      <div style={{ position: "relative" }}>
        <img src={resolveImg(item.img)} alt={item.title} style={{ width: "100%", height: 220, objectFit: "cover" }} onError={(e) => { const tgt = e.currentTarget; if (tgt.src !== logo) tgt.src = logo; }} />
        <div style={{ position: "absolute", top: 12, left: 12, background: "rgba(8,8,6,0.82)", border: `1px solid ${c.goldDark}88`, color: c.goldLight, borderRadius: 999, padding: "6px 10px", fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase", backdropFilter: "blur(4px)" }}>{badge}</div>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ color: c.gold, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{item.title}</div>
        <div style={{ color: c.muted, fontSize: 13, lineHeight: 1.7, marginBottom: 6 }}>{item.sub}</div>
        <div style={{ color: c.goldDark, fontSize: 12, fontStyle: "italic", letterSpacing: 0.3 }}>{item.context}</div>
      </div>
    </div>
  );
}

function ResultsSection({ t, onStart, onTalk }: { t: Translations; onStart: () => void; onTalk: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const r = t.results;
  const expandRef = useRef<HTMLDivElement>(null);
  const handleExpand = () => {
    if (expanded && expandRef.current) expandRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setExpanded(v => !v);
  };
  return (
    <section id="results" style={{ padding: "90px 20px 0", background: "linear-gradient(180deg, #0a0a08, #111008, #090907)" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <Divider /><SectionTitle label={r.label} title={r.title} subtitle={r.subtitle} />
        <div style={{ position: "relative" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 20 }}>
            {r.items.map((item) => <FadeIn key={item.img}><ResultCard item={item} badge={r.badge} /></FadeIn>)}
          </div>
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 72, background: "linear-gradient(transparent, #0d0d0a)", opacity: expanded ? 0 : 1, transition: "opacity 0.5s ease", pointerEvents: "none" }} />
        </div>
        {expanded && (
          <div ref={expandRef} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 20, marginTop: 20 }}>
            {r.moreItems.map((item, i) => <FadeIn key={i} delay={i * 110}><ResultCard item={item} badge={r.badge} /></FadeIn>)}
          </div>
        )}
        <div style={{ textAlign: "center", marginTop: 32, marginBottom: 60 }}>
          <button onClick={handleExpand}
            style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "13px 26px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${c.goldDark}22`; e.currentTarget.style.color = c.goldLight; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = c.gold; }}>
            {expanded ? r.showLess : r.exploreMore}
          </button>
        </div>
        <div style={{ paddingBottom: 90 }}>
          <DualCTABlock eyebrow={t.dualCta.postResults.eyebrow} headline={t.dualCta.postResults.headline} sub={t.dualCta.postResults.sub} primary={t.dualCta.postResults.primary} secondary={t.dualCta.postResults.secondary} onPrimary={onStart} onSecondary={onTalk} />
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

function ArrowButton({ direction, onClick }: { direction: "prev" | "next"; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: "transparent", border: `1px solid ${c.goldDark}`, borderRadius: "50%", width: 44, height: 44, color: c.gold, opacity: hovered ? 1 : 0.4, cursor: "pointer", fontFamily: "inherit", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", transition: "opacity 0.2s ease", flexShrink: 0 }}>
      {direction === "prev" ? "←" : "→"}
    </button>
  );
}

function TestimonialsCarousel({ items }: { items: Array<{ name: string; quote: string }> }) {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(0);
  const n = items.length;
  const pad = (x: number) => String(x).padStart(2, "0");
  const goPrev = () => setCurrent(c => (c - 1 + n) % n);
  const goNext = () => setCurrent(c => (c + 1) % n);
  const getRelativePos = (i: number) => { let diff = i - current; if (diff > n / 2) diff -= n; if (diff < -n / 2) diff += n; return diff; };
  return (
    <div style={{ position: "relative", userSelect: "none" }}>
      <div style={{ overflow: "hidden", position: "relative", padding: "8px 0 16px" }}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { const diff = touchStartX.current - e.changedTouches[0].clientX; if (Math.abs(diff) > 48) diff > 0 ? goNext() : goPrev(); }}>
        <div style={{ display: "flex", transform: `translateX(calc(${-current * 80}% + 10%))`, transition: "transform 0.4s ease-in-out", willChange: "transform" }}>
          {items.map((item, i) => {
            const pos = getRelativePos(i); const isCenter = pos === 0; const isAdjacent = Math.abs(pos) === 1;
            return (
              <div key={i} style={{ minWidth: "78%", padding: "0 10px", transition: "opacity 0.4s ease-in-out, transform 0.4s ease-in-out, filter 0.4s ease-in-out", opacity: isCenter ? 1 : isAdjacent ? 0.75 : 0.15, transform: `scale(${isCenter ? 1 : 0.91})`, filter: isCenter ? "none" : isAdjacent ? "blur(0.5px)" : "blur(3px)", transformOrigin: pos < 0 ? "right center" : "left center", pointerEvents: isCenter ? "auto" : "none" }}>
                <Card style={{ position: "relative", padding: 28, minHeight: 160 }}>
                  {isCenter && <div style={{ position: "absolute", top: 18, right: 20, color: c.muted, fontSize: 10, letterSpacing: 3, fontFamily: "inherit" }}>{pad(i + 1)} / {pad(n)}</div>}
                  <div style={{ color: c.gold, marginBottom: 12, fontSize: 18 }}>★★★★★</div>
                  <p style={{ color: c.soft, lineHeight: 1.85, fontSize: 15, fontStyle: "italic", margin: "0 0 16px", paddingRight: isCenter ? 44 : 0 }}>"{item.quote}"</p>
                  <div style={{ color: c.goldLight, fontSize: 15, fontWeight: 700 }}>{item.name}</div>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 20, marginTop: 8 }}>
        <ArrowButton direction="prev" onClick={goPrev} />
        <ArrowButton direction="next" onClick={goNext} />
      </div>
    </div>
  );
}

function GoogleTrustStrip({ t }: { t: Translations }) {
  const g = t.googleReview;
  return (
    <div style={{ maxWidth: 780, margin: "0 auto 40px", padding: "14px 22px", background: `${c.goldDark}0C`, border: `1px solid ${c.goldDark}2A`, borderRadius: 10, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
        <span style={{ color: "#F4B942", fontSize: 13, letterSpacing: 1 }}>★★★★★</span>
        <span style={{ color: c.text, fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>{g.rating}</span>
      </div>
      <div style={{ width: 1, height: 18, background: c.border, flexShrink: 0 }} />
      <p style={{ color: c.soft, fontSize: 13, fontStyle: "italic", margin: 0, flex: 1, minWidth: 180, textAlign: "center", lineHeight: 1.6 }}>"{g.quote}" — <span style={{ color: c.muted }}>{g.author}</span></p>
      <a href={g.url} target="_blank" rel="noreferrer"
        style={{ color: c.gold, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", textDecoration: "none", flexShrink: 0, opacity: 0.7, transition: "opacity 0.2s" }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}>
        {g.cta} ↗
      </a>
    </div>
  );
}

function TestimonialsSection({ t, onTestimonial, onStart, onTalk }: { t: Translations; onTestimonial: () => void; onStart: () => void; onTalk: () => void }) {
  const tm = t.testimonials;
  return (
    <section style={{ padding: "90px 20px 0" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14 }}>{tm.label}</div>
          <div style={{ height: 1, width: 48, background: `linear-gradient(90deg, transparent, ${c.gold}, transparent)`, margin: "0 auto" }} />
        </div>
        <p style={{ textAlign: "center", color: c.muted, fontSize: 15, fontStyle: "italic", margin: "0 auto 36px", maxWidth: 480, lineHeight: 1.7 }}>{tm.transitionLine}</p>
        <GoogleTrustStrip t={t} />
        <TestimonialsCarousel items={tm.items} />
        <p style={{ textAlign: "center", color: c.muted, fontSize: 14, fontStyle: "italic", margin: "40px auto 28px", maxWidth: 480, lineHeight: 1.7 }}>{tm.ctaBridge}</p>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <p style={{ color: c.muted, fontSize: 13, marginBottom: 14 }}>{t.testimonialsPage.modalTitle}</p>
          <button onClick={onTestimonial}
            style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "13px 24px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${c.goldDark}22`; e.currentTarget.style.color = c.goldLight; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = c.gold; }}>
            {t.testimonialsPage.shareBtn}
          </button>
        </div>
        <div style={{ paddingBottom: 90 }}>
          <DualCTABlock eyebrow={t.dualCta.postTestimonials.eyebrow} headline={t.dualCta.postTestimonials.headline} sub={t.dualCta.postTestimonials.sub} primary={t.dualCta.postTestimonials.primary} secondary={t.dualCta.postTestimonials.secondary} onPrimary={onStart} onSecondary={onTalk} />
        </div>
      </div>
    </section>
  );
}

// ─── Coach Photo Stack ────────────────────────────────────────────────────────

function CoachPhotoStack({ photos, coachName, tagline, delay, ratios }: { photos: string[]; coachName: string; tagline: string; delay: number; ratios: string[]; }) {
  return (
    <FadeIn delay={delay} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 14 }}>
        {photos.map((src, i) => (
          <div key={i} className={i === 2 ? "hide-on-mobile" : ""}
            style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${c.border}`, aspectRatio: ratios[i], transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.45)"; e.currentTarget.style.borderColor = `${c.goldDark}88`; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = c.border; }}>
            <img src={resolveImg(src)} alt={`${coachName} coaching`}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }}
              onError={(e) => {
                const target = e.currentTarget; target.style.display = "none";
                const parent = target.parentElement;
                if (parent && !parent.querySelector(".photo-placeholder")) {
                  const ph = document.createElement("div"); ph.className = "photo-placeholder";
                  ph.style.cssText = `width:100%;height:100%;min-height:200px;background:${c.card};display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;`;
                  ph.innerHTML = `<div style="color:${c.goldDark};font-size:28px">📸</div><div style="color:${c.muted};font-size:10px;letter-spacing:2px;text-transform:uppercase;text-align:center;padding:0 12px">Upload photo</div>`;
                  parent.appendChild(ph);
                }
              }} />
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", marginBottom: 8 }}>
          <div style={{ width: 28, height: 1, background: `linear-gradient(90deg, transparent, ${c.goldDark})` }} />
          <span style={{ color: c.gold, fontSize: 9 }}>❆</span>
          <div style={{ width: 28, height: 1, background: `linear-gradient(90deg, ${c.goldDark}, transparent)` }} />
        </div>
        <p style={{ color: c.text, fontSize: 20, fontWeight: 700, margin: "0 0 6px", letterSpacing: 1 }}>{coachName}</p>
        <p style={{ color: c.muted, fontSize: 12, letterSpacing: 3, textTransform: "uppercase", margin: 0 }}>{tagline}</p>
      </div>
    </FadeIn>
  );
}

function HowWeCoachSection({ t, onApply }: { t: Translations; onApply: () => void }) {
  const hwc = t.howWeCoach;
  return (
    <section style={{ padding: "90px 20px", background: c.black, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 0%, rgba(139,104,32,.10), transparent 60%)`, pointerEvents: "none" }} />
      <div style={{ maxWidth: 1120, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <FadeIn><Divider /><SectionTitle label={hwc.label} title={hwc.title} subtitle={hwc.subtitle} /></FadeIn>
        <FadeIn delay={60}><p style={{ textAlign: "center", color: c.muted, fontSize: 15, fontStyle: "italic", margin: "-16px auto 40px", maxWidth: 600, lineHeight: 1.75 }}>{hwc.anchorLine}</p></FadeIn>
        <div className="coach-action-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36, marginBottom: 52 }}>
          <CoachPhotoStack photos={kevinPhotos} coachName={hwc.kevinName} tagline={hwc.kevinTagline} delay={0} ratios={["3 / 4", "4 / 5", "1 / 1"]} />
          <CoachPhotoStack photos={yuneisynPhotos} coachName={hwc.yuneisynName} tagline={hwc.yuneisynTagline} delay={150} ratios={["1 / 1", "3 / 4", "4 / 5"]} />
        </div>
        <FadeIn delay={200}>
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, justifyContent: "center", marginBottom: 20 }}>
              <div style={{ width: 60, height: 1, background: `linear-gradient(90deg, transparent, ${c.goldDark})` }} />
              <span style={{ color: c.gold, fontSize: 12 }}>❆</span>
              <div style={{ width: 60, height: 1, background: `linear-gradient(90deg, ${c.goldDark}, transparent)` }} />
            </div>
            <p style={{ color: c.soft, fontSize: "clamp(15px,2.5vw,20px)", letterSpacing: 2, textTransform: "uppercase", margin: "0 0 28px", fontStyle: "italic" }}>{hwc.brandLine}</p>
            <button onClick={onApply}
              style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "12px 24px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${c.goldDark}22`; e.currentTarget.style.color = c.goldLight; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = c.gold; }}>
              {hwc.cta}
            </button>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Investment Section (Upgrade 3 — reframed coaching paths) ─────────────────

function InvestmentSection({ t, onApply }: { t: Translations; onApply: () => void }) {
  const inv = t.investment;
  return (
    <section style={{ maxWidth: 1120, margin: "0 auto", padding: "0 20px 90px" }}>
      <Divider />
      <p style={{ textAlign: "center", color: c.goldLight, fontSize: 13, letterSpacing: 4, textTransform: "uppercase", marginBottom: 20 }}>{inv.topLine}</p>
      <SectionTitle label={inv.label} title={inv.title} subtitle={inv.subtitle} />
      <p className="ky-invest-intro" style={{ color: c.soft, fontSize: 15, lineHeight: 1.9, maxWidth: 780, margin: "0 auto 48px", textAlign: "center" }}>{inv.intro}</p>

      {/* 3 coaching path cards */}
      <div className="ky-invest-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 22, marginBottom: 36 }}>
        {inv.cards.map((card, i) => {
          const isFeatured = i === 2; // In-Person = featured card
          return (
            <FadeIn key={card.title} delay={i * 100}>
              <div className="ky-invest-card" style={{
                background: isFeatured ? "linear-gradient(160deg, #0f0e08, #14120a)" : c.card,
                border: `1px solid ${isFeatured ? c.gold : `${c.goldDark}44`}`,
                borderRadius: 14,
                padding: "28px 24px",
                position: "relative",
                boxShadow: isFeatured ? `0 0 40px ${c.goldDark}28` : "none",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = isFeatured ? `0 20px 50px ${c.goldDark}44` : "0 16px 40px rgba(0,0,0,0.3)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = isFeatured ? `0 0 40px ${c.goldDark}28` : "none"; }}
              >
                {/* Badge chip */}
                <div style={{ display: "inline-flex", alignSelf: "flex-start", background: isFeatured ? `linear-gradient(135deg, ${c.goldDark}, ${c.gold})` : `${c.goldDark}22`, border: `1px solid ${isFeatured ? "transparent" : `${c.goldDark}55`}`, borderRadius: 999, padding: "4px 12px", marginBottom: 18 }}>
                  <span style={{ color: isFeatured ? c.black : c.goldLight, fontSize: 10, letterSpacing: 2, fontWeight: 900, textTransform: "uppercase" }}>{card.badge}</span>
                </div>

                <div style={{ fontSize: 28, marginBottom: 12 }}>{card.icon}</div>
                <h3 style={{ color: isFeatured ? c.gold : c.text, fontSize: 21, margin: "0 0 12px", fontWeight: 700 }}>{card.title}</h3>
                <p className="ky-invest-card-desc" style={{ color: c.muted, fontSize: 14, lineHeight: 1.85, margin: "0 0 20px", flex: 1 }}>{card.desc}</p>

                {/* Highlight bullets */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 0 }}>
                  {card.highlights.map((hl) => (
                    <div key={hl} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: c.gold, fontSize: 11, flexShrink: 0 }}>❆</span>
                      <span style={{ color: c.soft, fontSize: 13, lineHeight: 1.5 }}>{hl}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          );
        })}
      </div>

      <p style={{ textAlign: "center", color: c.muted, fontSize: 13, fontStyle: "italic", marginBottom: 32 }}>{inv.qualifier}</p>

      {/* Closing CTA block */}
      <div style={{ background: "linear-gradient(135deg, rgba(12,12,7,1), rgba(20,18,10,1), rgba(12,12,7,1))", border: `1px solid ${c.goldDark}44`, borderRadius: 16, padding: "32px 32px", textAlign: "center", maxWidth: 780, margin: "0 auto" }}>
        <p style={{ color: c.soft, fontSize: 15, lineHeight: 1.85, margin: "0 0 10px", fontStyle: "italic" }}>{inv.closingLine}</p>
        <p style={{ color: c.muted, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontStyle: "italic", margin: "0 0 22px" }}>{inv.microcopyPre}</p>
        <button onClick={onApply} style={{ background: `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})`, color: c.black, border: 0, borderRadius: 8, padding: "16px 32px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 13, animation: "glowPulse 3s ease-in-out infinite" }}>{inv.cta}</button>
        <p style={{ color: c.muted, fontSize: 12, fontStyle: "italic", marginTop: 12 }}>{inv.ctaMicrocopy}</p>
      </div>
    </section>
  );
}

// ─── WhoThisIsFor ─────────────────────────────────────────────────────────────

function WhoThisIsFor({ t, onApply }: { t: Translations; onApply: () => void }) {
  const wf = t.whoFor;
  return (
    <section style={{ maxWidth: 1120, margin: "0 auto", padding: "0 20px 90px" }}>
      <Divider /><SectionTitle label={wf.label} title={wf.title} subtitle={wf.subtitle} />
      <p style={{ textAlign: "center", color: c.goldLight, fontSize: 15, fontStyle: "italic", margin: "-20px auto 32px", maxWidth: 600 }}>{wf.introLine}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, marginBottom: 32 }}>
        {wf.items.map((item) => (
          <div key={item.title} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: "22px 20px", display: "flex", flexDirection: "column", gap: 10, transition: "border-color 0.2s ease" }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = c.goldDark)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = c.border)}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: c.gold, fontSize: 14 }}>{item.icon}</span>
              <span style={{ color: c.text, fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{item.title}</span>
            </div>
            <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.75, margin: 0 }}>{item.desc}</p>
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center" }}>
        <button onClick={onApply} style={{ background: `linear-gradient(135deg,${c.goldDark},${c.gold},${c.goldLight})`, color: c.black, border: 0, borderRadius: 8, padding: "16px 32px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>{wf.cta}</button>
        <p style={{ color: c.muted, fontSize: 12, marginTop: 10, fontStyle: "italic" }}>{wf.ctaMicrocopy}</p>
      </div>
    </section>
  );
}

// ─── Credentials ──────────────────────────────────────────────────────────────

function CredentialsSection({ t, onApply }: { t: Translations; onApply: () => void }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const cr = t.creds;
  return (
    <section style={{ maxWidth: 1120, margin: "0 auto", padding: "0 20px 90px" }}>
      <Divider /><SectionTitle label={cr.label} title={cr.title} subtitle={cr.subtitle} />
      <div style={{ background: `${c.goldDark}18`, border: `1px solid ${c.goldDark}44`, borderRadius: 12, padding: "18px 24px", maxWidth: 820, margin: "0 auto 36px", textAlign: "center" }}>
        <p style={{ color: c.soft, fontSize: 14, lineHeight: 1.85, margin: 0, fontStyle: "italic" }}>{cr.intro}</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
        {cr.coaches.map((coach) => (
          <Card key={coach.name} style={{ padding: 28 }}>
            <div style={{ marginBottom: 20, paddingBottom: 18, borderBottom: `1px solid ${c.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
                <div style={{ width: 46, height: 46, borderRadius: "50%", background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, color: c.black, flexShrink: 0 }}>{coach.name.charAt(0)}</div>
                <div>
                  <div style={{ color: c.text, fontSize: 18, fontWeight: 700 }}>{coach.name}</div>
                  <div style={{ color: c.gold, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginTop: 2 }}>{coach.role}</div>
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                <div style={{ display: "inline-block", background: `${c.goldDark}22`, border: `1px solid ${c.goldDark}55`, borderRadius: 999, padding: "5px 12px", color: c.goldLight, fontSize: 11, letterSpacing: 1 }}>{coach.years} {cr.yearsExp}</div>
                {coach.nbc6 && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(26,72,112,0.2)", border: "1px solid rgba(26,72,112,0.5)", borderRadius: 999, padding: "5px 12px" }}>
                    <span style={{ color: "#fff", fontWeight: 900, fontSize: 9, background: "linear-gradient(135deg, #1a4870, #0f2d4a)", borderRadius: 4, padding: "2px 5px" }}>NBC</span>
                    <span style={{ color: c.soft, fontSize: 10, letterSpacing: 1 }}>{cr.asSeenOnText}</span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: c.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>{cr.prevRoleLabel}:</span>
                <span style={{ color: c.soft, fontSize: 12 }}>{coach.prevRole}</span>
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <p style={{ color: c.muted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12, fontWeight: 700 }}>{cr.certsLabel}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {cr.certList.filter((cert) => coach.certList.includes(cert.abbr)).map((cert) => (
                  <div key={cert.abbr} onClick={() => setExpanded(expanded === cert.abbr ? null : cert.abbr)} style={{ background: c.bg, border: `1px solid ${expanded === cert.abbr ? c.goldDark : c.border}`, borderRadius: 8, padding: "10px 12px", cursor: "pointer", transition: "border-color 0.2s" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div><span style={{ color: c.text, fontSize: 13, fontWeight: 700 }}>{cert.full}</span><span style={{ color: c.goldDark, fontSize: 10, marginLeft: 8, letterSpacing: 1 }}>{cert.org}</span></div>
                      <span style={{ color: c.muted, fontSize: 11 }}>{expanded === cert.abbr ? "▲" : "▼"}</span>
                    </div>
                    {expanded === cert.abbr && <p style={{ color: c.soft, fontSize: 12, lineHeight: 1.7, margin: "8px 0 0", fontStyle: "italic" }}>{cert.plain}</p>}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p style={{ color: c.muted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12, fontWeight: 700 }}>{cr.specsLabel}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {coach.specs.map((s) => (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ color: c.gold, fontSize: 10 }}>❆</span>
                    <span style={{ color: c.soft, fontSize: 14, lineHeight: 1.5 }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 28 }}>
        <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.8, maxWidth: 600, margin: "0 auto 20px" }}>{cr.bottomText}</p>
        <button onClick={onApply} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "14px 24px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>{cr.workWithUs}</button>
      </div>
    </section>
  );
}

// ─── ApplyModal ───────────────────────────────────────────────────────────────

function ApplyModal({ onClose, t }: { onClose: () => void; t: Translations }) {
  const [data, setData] = useState<BasicFormData>({});
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const m = t.modal;
  const inputStyle: React.CSSProperties = { width: "100%", borderRadius: 8, background: c.bg, color: c.text, border: `1px solid ${c.border}`, padding: "12px 14px", fontFamily: "inherit", fontSize: 14, outline: "none" };
  const canSubmit = useMemo(() => Boolean((data.fullName || "").trim() && (data.email || "").trim() && (data.goal || "").trim()), [data]);
  const submit = async () => {
    if (!canSubmit) return; setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xdawlovn", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ type: "KY Website Question", "Full Name": data.fullName, Email: data.email, Phone: data.phone, "What They're Looking For": data.goal, "Preferred Coaching Format": data.trainingType, Source: "KY Website — Services Question", "Submission Date": new Date().toLocaleDateString() }) });
      if (!res.ok) throw new Error(); setStatus("success");
    } catch { setStatus("error"); }
  };
  const okBtn: React.CSSProperties = { background: canSubmit && status !== "sending" ? `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})` : `${c.goldDark}66`, border: 0, borderRadius: 8, padding: "14px 26px", color: c.black, cursor: canSubmit && status !== "sending" ? "pointer" : "not-allowed", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase" };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.92)", backdropFilter: "blur(8px)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 560, background: c.card, border: `1px solid ${c.goldDark}`, borderRadius: 16, padding: 28, boxShadow: "0 30px 80px rgba(0,0,0,.8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, gap: 16 }}>
          <div><p style={{ color: c.gold, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", marginBottom: 8 }}>{m.eyebrow}</p><h3 style={{ color: c.text, margin: 0, fontSize: 28 }}>{m.title}</h3></div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${c.border}`, background: "transparent", color: c.muted, cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        {status === "success" ? (
          <div>
            <p style={{ color: c.gold, fontSize: 12, letterSpacing: 4, textTransform: "uppercase", marginBottom: 12 }}>{m.successEyebrow}</p>
            <h4 style={{ color: c.text, fontSize: 30, margin: "0 0 12px" }}>{m.successTitle}</h4>
            <p style={{ color: c.soft, lineHeight: 1.85, fontSize: 15 }}>{m.successBody}</p>
            <button onClick={onClose} style={{ marginTop: 18, background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})`, border: 0, borderRadius: 8, padding: "14px 26px", color: c.black, cursor: "pointer", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase" }}>{m.backToSite}</button>
          </div>
        ) : (
          <>
            <p style={{ color: c.muted, lineHeight: 1.75, fontSize: 14, marginBottom: 22 }}>{m.sub}</p>
            <div style={{ display: "grid", gap: 16 }}>
              <Field label={m.fName}><input type="text" value={data.fullName || ""} placeholder={m.phName} onChange={(e) => setData({ ...data, fullName: e.target.value })} style={inputStyle} /></Field>
              <Field label={m.fEmail}><input type="email" value={data.email || ""} placeholder={m.phEmail} onChange={(e) => setData({ ...data, email: e.target.value })} style={inputStyle} /></Field>
              <Field label={m.fPhone}><input type="tel" value={data.phone || ""} placeholder={m.phPhone} onChange={(e) => setData({ ...data, phone: e.target.value })} style={inputStyle} /></Field>
              <Field label={m.fGoal}><select value={data.goal || ""} onChange={(e) => setData({ ...data, goal: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}><option value="">{m.phGoal}</option>{t.goals.map((g) => <option key={g} value={g}>{g}</option>)}</select></Field>
              <Field label={m.fStyle}><select value={data.trainingType || ""} onChange={(e) => setData({ ...data, trainingType: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}><option value="">{m.phStyle}</option>{t.coachingTypes.map((tp) => <option key={tp} value={tp}>{tp}</option>)}</select></Field>
            </div>
            {status === "error" && <div style={{ marginTop: 16, border: "1px solid #8f2d2d", background: "#170808", borderRadius: 8, padding: "12px 14px", color: "#ef8d8d", fontSize: 13 }}>{m.error}</div>}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
              <p style={{ color: c.muted, fontSize: 12, margin: 0 }}>{m.location}</p>
              <button onClick={submit} disabled={!canSubmit || status === "sending"} style={okBtn}>{status === "sending" ? m.sending : m.submit}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ReferralPage ─────────────────────────────────────────────────────────────

function ReferralPage({ onBack, t }: { onBack: () => void; t: Translations }) {
  const [refCode, setRefCode] = useState(""); const [refName, setRefName] = useState("");
  const [data, setData] = useState<ReferralFormData>({}); const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const rp = t.rp;
  const inputStyle: React.CSSProperties = { width: "100%", borderRadius: 8, background: c.bg, color: c.text, border: `1px solid ${c.border}`, padding: "12px 14px", fontFamily: "inherit", fontSize: 14, outline: "none" };
  const canSubmit = Boolean((data.fullName || "").trim() && (data.email || "").trim());
  const handleCode = (v: string) => { const n = v.toUpperCase().trim(); setRefCode(n); setRefName(referralCodes[n] || ""); };
  const submit = async () => {
    if (!canSubmit) return; setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xgonjarg", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ "Form Type": "Referral Submission", "New Client Name": data.fullName, "New Client Email": data.email, "New Client Phone": data.phone, "New Client Goal": data.goal, "Referral Code Used": refCode || "None", "Referred By": refName || "Not specified", "Referral Reward": "One free session", "Submission Date": new Date().toLocaleDateString() }) });
      if (!res.ok) throw new Error(); setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return (
    <div style={{ minHeight: "100vh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Georgia, serif" }}>
      <div style={{ textAlign: "center", maxWidth: 520 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
        <h1 style={{ color: c.gold, fontSize: 38, letterSpacing: 4, margin: "0 0 12px" }}>{rp.successTitle}</h1>
        <p style={{ color: c.text, fontSize: 17, marginBottom: 12 }}>{rp.successWelcome} <span style={{ color: c.goldLight, fontWeight: "bold" }}>{data.fullName}</span>!</p>
        {refName && <Card style={{ marginBottom: 16, padding: 16 }}><p style={{ color: c.muted, fontSize: 13, margin: 0 }}>{rp.successRefBy} <span style={{ color: c.goldLight, fontWeight: "bold" }}>{refName}</span>. {rp.successReward}</p></Card>}
        <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.8, marginBottom: 24 }}>{rp.successNote}</p>
        <button onClick={onBack} style={{ background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})`, border: "none", borderRadius: 8, padding: "14px 32px", color: c.black, fontSize: 12, fontWeight: 900, cursor: "pointer", letterSpacing: 3, textTransform: "uppercase" }}>{rp.backToSite}</button>
      </div>
    </div>
  );
  return (
    <div style={{ minHeight: "100vh", background: c.bg, padding: "40px 16px 60px", fontFamily: "Georgia, serif" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <button onClick={onBack} style={{ background: "transparent", border: "none", cursor: "pointer", marginBottom: 20 }}><img src={logo} alt="KY" style={{ width: 90, height: 90, objectFit: "contain" }} /></button>
          <SectionTitle label={rp.label} title={rp.title} subtitle={rp.subtitle} />
        </div>
        <div style={{ marginBottom: 48 }}>
          <Card style={{ textAlign: "center", borderColor: `${c.goldDark}44`, maxWidth: 300, margin: "0 auto" }}>
            <p style={{ color: c.goldDark, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8, fontWeight: "bold" }}>{rp.rewardLabel}</p>
            <p style={{ color: c.text, fontSize: 16, fontWeight: 700, margin: 0 }}>{rp.rewardValue}</p>
          </Card>
          <p style={{ color: c.muted, fontSize: 12, textAlign: "center", marginTop: 14, letterSpacing: 1 }}>{rp.rewardNote}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 28 }}>
          <Card>
            <h3 style={{ color: c.gold, fontSize: 20, letterSpacing: 2, marginBottom: 8 }}>{rp.shareTitle}</h3>
            <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.8, marginBottom: 20 }}>{rp.shareBody}</p>
            <a href="https://instagram.com/kyempoweredfitness" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, background: c.bg, border: `1px solid ${c.goldDark}55`, borderRadius: 8, padding: "14px 16px", textDecoration: "none", marginBottom: 16 }}>
              <span style={{ fontSize: 20 }}>📸</span>
              <div><p style={{ color: c.muted, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", marginBottom: 2 }}>{rp.getCode}</p><p style={{ color: c.gold, fontSize: 14, fontWeight: 700, margin: 0 }}>@kyempoweredfitness</p></div>
            </a>
            <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: 14 }}><p style={{ color: c.soft, fontSize: 12, lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>{rp.sampleQuote}</p></div>
          </Card>
          <Card>
            <h3 style={{ color: c.gold, fontSize: 20, letterSpacing: 2, marginBottom: 8 }}>{rp.newTitle}</h3>
            <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.8, marginBottom: 20 }}>{rp.newBody}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label={rp.fName}><input type="text" style={inputStyle} placeholder={rp.phName} value={data.fullName || ""} onChange={(e) => setData({ ...data, fullName: e.target.value })} /></Field>
              <Field label={rp.fEmail}><input type="email" style={inputStyle} placeholder={rp.phEmail} value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })} /></Field>
              <Field label={rp.fPhone}><input type="tel" style={inputStyle} placeholder={rp.phPhone} value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} /></Field>
              <Field label={rp.fGoal}><select style={{ ...inputStyle, cursor: "pointer" }} value={data.goal || ""} onChange={(e) => setData({ ...data, goal: e.target.value })}><option value="">{rp.phGoal}</option>{t.goals.map((g) => <option key={g} value={g}>{g}</option>)}</select></Field>
              <Field label={rp.fCode}>
                <input type="text" style={inputStyle} placeholder={rp.phCode} value={refCode} onChange={(e) => handleCode(e.target.value)} />
                {refCode && refName && <p style={{ color: c.goldLight, fontSize: 12, marginTop: 6 }}>{rp.codeValid} <strong>{refName}</strong>.</p>}
                {refCode && !refName && refCode.length > 3 && <p style={{ color: "#e74c3c", fontSize: 12, marginTop: 6 }}>{rp.codeInvalid}</p>}
              </Field>
            </div>
            {status === "error" && <div style={{ background: "#1a0505", border: "1px solid #c0392b", borderRadius: 8, padding: "12px 14px", marginTop: 16 }}><p style={{ color: "#e74c3c", fontSize: 12, margin: 0 }}>{rp.error}</p></div>}
            <button onClick={submit} disabled={!canSubmit || status === "sending"} style={{ width: "100%", marginTop: 20, background: !canSubmit || status === "sending" ? `${c.goldDark}66` : `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`, border: "none", borderRadius: 8, padding: 16, color: c.black, fontSize: 13, fontWeight: 900, cursor: !canSubmit || status === "sending" ? "not-allowed" : "pointer", letterSpacing: 3, textTransform: "uppercase" }}>
              {status === "sending" ? rp.submitting : rp.submit}
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Testimonials Form + Page + Modal ─────────────────────────────────────────

type TestimonialData = { name?: string; coachingType?: string; duration?: string; rating?: number; review?: string; photoUrl?: string; photoFile?: File; permission?: boolean; };

function TestimonialsForm({ t, onSuccess, onBack }: { t: Translations; onSuccess: () => void; onBack?: () => void }) {
  const [data, setData] = useState<TestimonialData>({}); const [status, setStatus] = useState<"idle" | "sending" | "error">("idle"); const [permError, setPermError] = useState(false);
  const tp = t.testimonialsPage;
  const inputStyle: React.CSSProperties = { width: "100%", borderRadius: 8, background: c.bg, color: c.text, border: `1px solid ${c.border}`, padding: "12px 14px", fontFamily: "inherit", fontSize: 14, outline: "none" };
  const canSubmit = Boolean((data.name || "").trim() && (data.coachingType || "").trim() && (data.duration || "").trim() && (data.rating || 0) > 0 && (data.review || "").trim().length > 10);
  const submit = async () => {
    if (!canSubmit) return;
    if (!data.permission) { setPermError(true); return; }
    setPermError(false); setStatus("sending");
    try {
      const formData = new FormData();
      formData.append("Form Type", "Client Testimonial"); formData.append("Client Name", data.name || ""); formData.append("Coaching Type", data.coachingType || ""); formData.append("Training Duration", data.duration || "");
      formData.append("Star Rating", `${data.rating || 0}/5 — ${tp.ratingLabels[data.rating || 0] || ""}`); formData.append("Review", data.review || ""); formData.append("Photo URL", data.photoUrl || "Not provided"); formData.append("Permission Granted", data.permission ? "Yes" : "No"); formData.append("Submission Date", new Date().toLocaleDateString());
      if (data.photoFile) formData.append("photo", data.photoFile);
      const res = await fetch("https://formspree.io/f/mjgaaqbp", { method: "POST", headers: { Accept: "application/json" }, body: formData });
      if (!res.ok) throw new Error(); onSuccess();
    } catch { setStatus("error"); }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <label style={{ display: "block", color: c.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10, fontWeight: 700 }}>{tp.fields.rating}</label>
        <div style={{ display: "flex", gap: 8 }}>
          {[1,2,3,4,5].map((star) => <button key={star} onClick={() => setData({ ...data, rating: star })} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 32, padding: "0 2px", color: (data.rating || 0) >= star ? c.gold : c.border, transform: (data.rating || 0) >= star ? "scale(1.15)" : "scale(1)" }}>★</button>)}
          {data.rating ? <span style={{ color: c.goldLight, fontSize: 13, alignSelf: "center", marginLeft: 6, fontStyle: "italic" }}>{tp.ratingLabels[data.rating]}</span> : null}
        </div>
      </div>
      <Field label={tp.fields.name}><input type="text" style={inputStyle} placeholder={tp.fields.namePh} value={data.name || ""} onChange={(e) => setData({ ...data, name: e.target.value })} /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label={tp.fields.coachingType}><select style={{ ...inputStyle, cursor: "pointer" }} value={data.coachingType || ""} onChange={(e) => setData({ ...data, coachingType: e.target.value })}><option value="">{tp.fields.coachingTypePh}</option>{tp.coachingTypes.map((ct) => <option key={ct} value={ct}>{ct}</option>)}</select></Field>
        <Field label={tp.fields.duration}><select style={{ ...inputStyle, cursor: "pointer" }} value={data.duration || ""} onChange={(e) => setData({ ...data, duration: e.target.value })}><option value="">{tp.fields.durationPh}</option>{tp.durations.map((d) => <option key={d} value={d}>{d}</option>)}</select></Field>
      </div>
      <Field label={tp.fields.review}><textarea style={{ ...inputStyle, minHeight: 120, resize: "vertical", lineHeight: 1.7 }} placeholder={tp.fields.reviewPh} value={data.review || ""} onChange={(e) => setData({ ...data, review: e.target.value })} /></Field>
      <div>
        <Field label={tp.fields.photoUrl}><input type="url" style={inputStyle} placeholder={tp.fields.photoUrlPh} value={data.photoUrl || ""} onChange={(e) => setData({ ...data, photoUrl: e.target.value })} /></Field>
        <p style={{ color: c.muted, fontSize: 11, marginTop: 6, fontStyle: "italic" }}>{tp.fields.photoNote}</p>
      </div>
      <div>
        <Field label={tp.fields.photoUpload}><input type="file" accept="image/*" style={{ ...inputStyle, padding: "10px 14px", cursor: "pointer" }} onChange={(e) => { const file = e.target.files?.[0]; if (file) setData({ ...data, photoFile: file }); }} /></Field>
        <p style={{ color: c.muted, fontSize: 11, marginTop: 6, fontStyle: "italic" }}>{tp.fields.photoUploadNote}</p>
      </div>
      <div onClick={() => { setData({ ...data, permission: !data.permission }); setPermError(false); }} style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", background: data.permission ? `${c.goldDark}18` : c.bg, border: `1px solid ${permError ? "#c0392b" : data.permission ? c.goldDark : c.border}`, borderRadius: 8, padding: "14px 16px", transition: "all 0.2s" }}>
        <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${data.permission ? c.gold : c.muted}`, background: data.permission ? c.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
          {data.permission && <span style={{ color: c.black, fontSize: 12, fontWeight: 900 }}>✓</span>}
        </div>
        <span style={{ color: c.soft, fontSize: 13, lineHeight: 1.6 }}>{tp.fields.permission}</span>
      </div>
      {permError && <p style={{ color: "#e74c3c", fontSize: 12, marginTop: -12 }}>{tp.permissionRequired}</p>}
      {status === "error" && <div style={{ border: "1px solid #8f2d2d", background: "#170808", borderRadius: 8, padding: "12px 14px", color: "#ef8d8d", fontSize: 13 }}>{tp.error}</div>}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        {onBack && <button onClick={onBack} style={{ background: "transparent", border: `1px solid ${c.border}`, borderRadius: 8, color: c.muted, padding: "14px 20px", cursor: "pointer", fontFamily: "inherit", letterSpacing: 1, fontSize: 12 }}>← {t.story.back}</button>}
        <button onClick={submit} disabled={!canSubmit || status === "sending"} style={{ flex: 1, background: !canSubmit || status === "sending" ? `${c.goldDark}66` : `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`, border: 0, borderRadius: 8, padding: "16px 24px", color: c.black, cursor: !canSubmit || status === "sending" ? "not-allowed" : "pointer", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", fontFamily: "inherit", fontSize: 13 }}>
          {status === "sending" ? tp.submitting : tp.submit}
        </button>
      </div>
    </div>
  );
}

function TestimonialsPage({ onBack, t }: { onBack: () => void; t: Translations }) {
  const [submitted, setSubmitted] = useState(false); const tp = t.testimonialsPage;
  if (submitted) return (
    <div style={{ minHeight: "100vh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Georgia, serif" }}>
      <div style={{ textAlign: "center", maxWidth: 520 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🙏</div>
        <h1 style={{ color: c.gold, fontSize: 38, letterSpacing: 3, margin: "0 0 16px" }}>{tp.successTitle}</h1>
        <p style={{ color: c.text, fontSize: 16, lineHeight: 1.85, marginBottom: 12 }}>{tp.successBody}</p>
        <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.8, marginBottom: 28 }}>{tp.successNote}</p>
        <button onClick={onBack} style={{ background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})`, border: "none", borderRadius: 8, padding: "14px 32px", color: c.black, fontSize: 12, fontWeight: 900, cursor: "pointer", letterSpacing: 3, textTransform: "uppercase" }}>{tp.backToSite}</button>
      </div>
    </div>
  );
  return (
    <div style={{ minHeight: "100vh", background: c.bg, padding: "40px 16px 80px", fontFamily: "Georgia, serif" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 42 }}>
          <button onClick={onBack} style={{ background: "transparent", border: "none", cursor: "pointer", marginBottom: 18 }}><img src={logo} alt="KY Empowered Fitness" style={{ width: 80, height: 80, objectFit: "contain" }} /></button>
          <SectionTitle label={tp.label} title={tp.title} subtitle={tp.subtitle} />
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: `${c.goldDark}18`, border: `1px solid ${c.goldDark}44`, borderRadius: 999, padding: "8px 16px", marginTop: 8 }}>
            <span style={{ color: c.gold, fontSize: 12 }}>❆</span>
            <span style={{ color: c.muted, fontSize: 12, fontStyle: "italic" }}>{tp.note}</span>
          </div>
        </div>
        <Card style={{ padding: 32 }}><TestimonialsForm t={t} onSuccess={() => setSubmitted(true)} onBack={onBack} /></Card>
      </div>
    </div>
  );
}

function TestimonialsModal({ onClose, t }: { onClose: () => void; t: Translations }) {
  const [submitted, setSubmitted] = useState(false); const tp = t.testimonialsPage;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.92)", backdropFilter: "blur(8px)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 620, background: c.card, border: `1px solid ${c.goldDark}`, borderRadius: 16, padding: 28, boxShadow: "0 30px 80px rgba(0,0,0,.8)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div><p style={{ color: c.gold, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", marginBottom: 6 }}>{tp.label}</p><h3 style={{ color: c.text, margin: 0, fontSize: 26, lineHeight: 1.2 }}>{tp.title}</h3></div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${c.border}`, background: "transparent", color: c.muted, cursor: "pointer", fontSize: 18, flexShrink: 0 }}>×</button>
        </div>
        {submitted ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🙏</div>
            <h4 style={{ color: c.gold, fontSize: 28, margin: "0 0 12px" }}>{tp.successTitle}</h4>
            <p style={{ color: c.soft, lineHeight: 1.85, fontSize: 15, marginBottom: 8 }}>{tp.successBody}</p>
            <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.8, marginBottom: 22 }}>{tp.successNote}</p>
            <button onClick={onClose} style={{ background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})`, border: 0, borderRadius: 8, padding: "14px 26px", color: c.black, cursor: "pointer", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase" }}>{tp.backToSite}</button>
          </div>
        ) : (
          <>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: `${c.goldDark}18`, border: `1px solid ${c.goldDark}44`, borderRadius: 999, padding: "6px 14px", marginBottom: 22 }}>
              <span style={{ color: c.gold, fontSize: 11 }}>❆</span>
              <span style={{ color: c.muted, fontSize: 11, fontStyle: "italic" }}>{tp.note}</span>
            </div>
            <TestimonialsForm t={t} onSuccess={() => setSubmitted(true)} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── StoryPage ────────────────────────────────────────────────────────────────

function StoryPage({ onBack, t }: { onBack: () => void; t: Translations }) {
  const s = t.story;
  return (
    <div style={{ minHeight: "100vh", background: c.bg, padding: "40px 16px 60px", fontFamily: "Georgia, serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 42 }}>
          <button onClick={onBack} style={{ background: "transparent", border: "none", cursor: "pointer", marginBottom: 18 }}><img src={logo} alt="KY" style={{ width: 88, height: 88, objectFit: "contain" }} /></button>
          <SectionTitle label={s.label} title={s.title} subtitle={s.subtitle} />
        </div>
        <Card style={{ padding: 0, overflow: "hidden", marginBottom: 28 }}>
          <div className="ky-story-hero-grid" style={{ display: "grid", gridTemplateColumns: "minmax(280px, 0.95fr) minmax(320px, 1.05fr)", alignItems: "stretch" }}>
            <div className="ky-story-image" style={{ minHeight: 420, background: c.bg }}><img src={couplePhoto} alt="Kevin and Yuneisy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={(e) => { const tgt = e.currentTarget; tgt.src = logo; tgt.style.objectFit = "contain"; tgt.style.padding = "40px"; }} /></div>
            <div className="ky-story-text" style={{ padding: "34px 30px" }}>
              <p style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 12 }}>{s.coachLabel}</p>
              <h2 style={{ color: c.text, fontSize: "clamp(28px,4vw,42px)", lineHeight: 1.08, margin: "0 0 16px" }}>{s.heroTitle}</h2>
              <p style={{ color: c.soft, lineHeight: 1.9, fontSize: 16, margin: 0 }}>{s.heroBody}</p>
            </div>
          </div>
        </Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 22 }}>
          {s.sections.map((sec) => <Card key={sec.title}><h3 style={{ color: c.gold, fontSize: 24, margin: "0 0 12px" }}>{sec.title}</h3><p style={{ color: c.muted, lineHeight: 1.9, fontSize: 15, margin: 0 }}>{sec.body}</p></Card>)}
        </div>
        <div style={{ textAlign: "center", marginTop: 34 }}>
          <button onClick={onBack} style={{ background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})`, border: 0, borderRadius: 8, padding: "14px 28px", color: c.black, cursor: "pointer", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase" }}>{s.back}</button>
        </div>
      </div>
    </div>
  );
}

// ─── BookPage ─────────────────────────────────────────────────────────────────

function BookPage({ onBack, onCalendly, t, lang, calendlyUrl }: { onBack: () => void; onCalendly: () => void; t: Translations; lang: string; calendlyUrl: string }) {
  const embedRef = useRef<HTMLDivElement>(null);
  const bk = t.booking;

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const timer = setTimeout(() => {
      if (window.Calendly && embedRef.current && embedRef.current.children.length === 0) {
        window.Calendly.initInlineWidget({
          url: calendlyUrl,
          parentElement: embedRef.current,
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [calendlyUrl]);

  return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      {/* Back nav */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(8,8,6,.96)", borderBottom: `1px solid ${c.border}`, backdropFilter: "blur(12px)" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 20px", minHeight: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 10, border: 0, background: "transparent", cursor: "pointer", color: c.text, fontFamily: "inherit", padding: 0 }}>
            <img src={logo} alt="KY" style={{ width: 36, height: 36, objectFit: "contain" }} />
            <span style={{ color: c.gold, fontSize: 11, letterSpacing: 3, textTransform: "uppercase" }}>KY Empowered Fitness</span>
          </button>
          <button onClick={onBack} style={{ border: `1px solid ${c.border}`, background: "transparent", color: c.muted, borderRadius: 8, padding: "8px 16px", fontFamily: "inherit", fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
            {bk?.bookPageBack || "← Back to Home"}
          </button>
        </div>
      </nav>

      {/* Header */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "56px 20px 32px", textAlign: "center" }}>
        <p style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14 }}>{bk?.label || "Free Strategy Call"}</p>
        <h1 style={{ color: c.text, fontSize: "clamp(28px,5vw,48px)", lineHeight: 1.08, margin: "0 0 16px" }}>
          {bk?.bookPageHeadline || "Let's Build Your Transformation Plan"}
        </h1>
        <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, marginBottom: 12 }}>
          {bk?.bookPageSub || "In 30 minutes, we'll break down your goals, your current situation, and exactly what it would take to get you results."}
        </p>
        <p style={{ color: c.muted, fontSize: 12, letterSpacing: 0.5 }}>❆ {bk?.trust || "Spots are limited each week to ensure quality coaching."}</p>
      </div>

      {/* Inline Calendly embed */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 16px 60px" }}>
        <div
          ref={embedRef}
          style={{ minHeight: 650, borderRadius: 14, overflow: "hidden", border: `1px solid ${c.border}`, background: "#fff" }}
        />
        {/* Fallback button if embed fails */}
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <p style={{ color: c.muted, fontSize: 13, marginBottom: 12 }}>
            {lang === "es" ? "¿No ves el calendario?" : "Don't see the calendar?"}
          </p>
          <button
            onClick={onCalendly}
            style={{ background: `linear-gradient(135deg,${c.goldDark},${c.gold})`, color: c.black, border: 0, borderRadius: 8, padding: "14px 28px", fontWeight: 900, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}
          >
            {bk?.cta || "Schedule My Call"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Homepage Export ──────────────────────────────────────────────────────

export default function KYHomepageRewrite() {
  const [lang, setLang] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem("ky-lang");
      if (stored === "en" || stored === "es") return stored;
    } catch {}
    const browser = navigator.language?.slice(0, 2).toLowerCase();
    return browser === "es" ? "es" : "en";
  });

  const [route, setRoute] = useState<string>(() => {
    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    return path;
  });

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showTestimonialsModal, setShowTestimonialsModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollCTA, setShowScrollCTA] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const t = lang === "en" ? en : es;

  const setLangPersisted = (l: Lang) => {
    setLang(l);
    try { localStorage.setItem("ky-lang", l); } catch {}
  };

  const [showFloat, setShowFloat] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const docH = document.documentElement.scrollHeight;
      const winH = window.innerHeight;
      const nearBottom = scrollY + winH > docH - 600; // hide float near booking/contact
      setScrolled(scrollY > 40);
      setShowScrollCTA(scrollY > 500);
      setShowFloat(scrollY > 500 && !nearBottom);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const normalize = (p: string) => p.replace(/\/+$/, "") || "/";
    const onPop = () => {
      setRoute(normalize(window.location.pathname));
      setMobileMenuOpen(false);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, "", path);
    setRoute(path.replace(/\/+$/, "") || "/");
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, left: 0 });
  };

  // Scroll to top on every route change (catches popstate too)
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [route]);

  // Load Calendly widget script
  useEffect(() => {
    if (document.getElementById("calendly-script")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.id = "calendly-script";
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.head.appendChild(script);
    return () => {};
  }, []);

  const goHome = () => navigate("/");

  const CALENDLY = "https://calendly.com/kyempoweredfitness/30min";

  const openCalendly = () => {
    setMobileMenuOpen(false);
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY });
    } else {
      window.open(CALENDLY, "_blank", "noopener,noreferrer");
    }
  };

  const openApply = () => { openCalendly(); };
  const openTestimonials = () => navigate("/testimonials");
  const openTalk = () => navigate("/talk");
  const openStart = () => navigate("/start");
  const openIntake = () => navigate("/intake");
  const openBook = () => navigate("/book");

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    if (id === "about-us") { navigate("/about-us"); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Route guards
  if (route === "/start") return <StartPage onBack={goHome} t={t} />;
  if (route === "/talk") return <TalkPage onBack={goHome} t={t} onStart={openStart} onIntake={openIntake} />;
  if (route === "/referral") return <ReferralPage onBack={goHome} t={t} />;
  if (route === "/about-us") return <StoryPage onBack={goHome} t={t} />;
  if (route === "/testimonials") return <TestimonialsPage onBack={goHome} t={t} />;
  if (route === "/book") return <BookPage onBack={goHome} onCalendly={openCalendly} t={t} lang={lang} calendlyUrl={CALENDLY} />;
  if (route === "/checkin") return <div style={{ minHeight: "100vh", background: c.bg }}><KYWeeklyCheckIn /></div>;
  if (route === "/intake") return (
    <div style={{ minHeight: "100vh", background: c.bg }}>
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(8,8,6,.96)", borderBottom: `1px solid ${c.border}`, backdropFilter: "blur(12px)" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 20px", minHeight: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 10, border: 0, background: "transparent", cursor: "pointer", color: c.text, fontFamily: "inherit", padding: 0 }}>
            <img src={logo} alt="KY" style={{ width: 36, height: 36, objectFit: "contain" }} />
            <span style={{ color: c.gold, fontSize: 11, letterSpacing: 3, textTransform: "uppercase" }}>KY Empowered Fitness</span>
          </button>
          <button onClick={() => navigate("/start")} style={{ border: `1px solid ${c.border}`, background: "transparent", color: c.muted, borderRadius: 8, padding: "8px 16px", fontFamily: "inherit", fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
            {lang === "es" ? "← Volver a Empezar" : "← Back to Start"}
          </button>
        </div>
      </nav>
      <KYIntakeSystem />
    </div>
  );

  const primaryBtn: React.CSSProperties = {
    background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`,
    color: c.black, border: 0, borderRadius: 8, padding: "16px 28px",
    fontWeight: 900, letterSpacing: 2, textTransform: "uppercase",
    cursor: "pointer", fontFamily: "inherit",
  };

  const navLinks = [
    { id: "about", label: t.nav.about },
    { id: "about-us", label: t.nav.story },
    { id: "results", label: t.nav.results },
    { id: "services", label: t.nav.services },
    { id: "contact", label: t.nav.contact },
  ];

  const h = t.hero;

  return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "Georgia, serif" }}>
      <style>{`
        *{box-sizing:border-box} html{scroll-behavior:smooth} body{margin:0} a{color:inherit}
        @keyframes glowPulse{0%,100%{box-shadow:0 0 20px rgba(139,104,32,.35)}50%{box-shadow:0 0 38px rgba(201,151,58,.52)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}

        /* ── DESKTOP NAV TOGGLE ── */
        @media(max-width:820px){.desktop-nav{display:none!important}.mobile-toggle{display:inline-flex!important}}
        @media(min-width:821px){.mobile-panel{display:none!important}}

        /* ── HERO GRID COLLAPSE ── */
        @media(max-width:820px){
          .hero-grid{grid-template-columns:1fr!important}
          .hero-copy{text-align:center!important}
          .hero-actions,.stats-row{justify-content:center!important}
          .ky-hero-card{display:none!important}
        }

        /* ── MOBILE OPTIMIZATIONS (<768px) ── */
        @media(max-width:767px){

          /* Hero — fit in one screen */
          .ky-hero{padding:88px 16px 36px!important;min-height:auto!important;align-items:flex-start!important}
          .ky-hero-eyebrow{font-size:9px!important;letter-spacing:3px!important;margin-bottom:8px!important}
          .ky-hero-headline{font-size:clamp(26px,8vw,38px)!important;line-height:1.06!important;margin-bottom:12px!important}
          .ky-hero-sub{font-size:14px!important;line-height:1.7!important;margin-bottom:18px!important}
          .ky-hero-microcopy{display:none!important}
          .ky-hero-stats{margin-top:20px!important;gap:18px!important}
          .ky-hero-statval{font-size:22px!important}

          /* Hero CTAs — side by side, full width pair */
          .hero-actions{flex-direction:row!important;gap:8px!important;margin-bottom:10px!important}
          .hero-actions > button{flex:1!important;min-width:0!important;padding:14px 10px!important;font-size:11px!important;letter-spacing:1px!important;min-height:48px!important}

          /* Global section padding */
          .ky-section{padding:52px 16px!important}
          .ky-section-wide{padding:52px 16px!important}

          /* Section titles on mobile */
          .ky-section-title h2{font-size:clamp(22px,6.5vw,30px)!important;line-height:1.1!important}
          .ky-section-title p{font-size:13px!important;line-height:1.7!important;margin-top:10px!important}

          /* Transformation System — 2 col grid */
          .ky-system-grid{grid-template-columns:1fr 1fr!important;gap:10px!important;margin-bottom:28px!important}
          .ky-system-card{padding:14px 12px!important;gap:8px!important}
          .ky-system-card-icon{width:34px!important;height:34px!important;font-size:15px!important;border-radius:8px!important}
          .ky-system-card-title{font-size:13px!important;line-height:1.3!important}
          .ky-system-card-desc{font-size:12px!important;line-height:1.55!important}

          /* Investment — full width, compact */
          .ky-invest-grid{grid-template-columns:1fr!important;gap:14px!important}
          .ky-invest-card{padding:20px 18px!important}
          .ky-invest-card-desc{font-size:13px!important;line-height:1.7!important;margin-bottom:14px!important}
          .ky-invest-intro{font-size:13px!important;line-height:1.7!important;margin-bottom:28px!important}

          /* Services grid */
          .ky-services-grid{gap:12px!important}
          .ky-service-card{padding:18px!important}

          /* About grid */
          .ky-about-grid{gap:12px!important}
          .ky-about-card{padding:18px!important}

          /* Nav mobile items */
          .ky-nav-item{padding:14px 16px!important;font-size:13px!important;letter-spacing:0.5px!important}
          .ky-nav-apply{padding:15px 16px!important;min-height:48px!important}

          /* Primary/ghost buttons — 48px tap target */
          .ky-btn-primary{min-height:48px!important;padding:14px 22px!important}
          .ky-btn-ghost{min-height:48px!important;padding:14px 22px!important}

          /* Sticky CTA — safe area */
          .ky-sticky-cta{
            padding:12px 16px!important;
            padding-bottom:calc(12px + env(safe-area-inset-bottom,0px))!important;
            box-shadow:0 -6px 24px rgba(0,0,0,0.65)!important
          }
          .ky-sticky-cta button{min-height:48px!important;font-size:11px!important}

          /* Trust bar */
          .ky-trust-bar{padding:10px 16px!important}

          /* Dividers */
          .ky-divider{margin-bottom:24px!important}

          /* Section CTA blocks */
          .ky-cta-block{padding:28px 18px!important;border-radius:14px!important}
          .ky-cta-block h2{font-size:clamp(20px,6vw,26px)!important}
          .ky-cta-block p{font-size:13px!important}

          /* Form inputs */
          .ky-form-input{font-size:16px!important;padding:13px 14px!important}

          /* Footer links */
          .ky-footer-links{flex-direction:column!important;align-items:center!important;gap:8px!important}

          /* Referral section */
          .ky-referral-btns{flex-direction:column!important;align-items:stretch!important}
          .ky-referral-btns button{width:100%!important;min-height:48px!important}

          /* Who This Is For grid */
          .ky-who-grid{grid-template-columns:1fr!important;gap:10px!important}
        }

        /* ── SMALL PHONE (<390px) ── */
        @media(max-width:389px){
          .ky-hero-headline{font-size:24px!important}
          .ky-system-grid{grid-template-columns:1fr!important}
        }

        /* ── FLOATING BOOK BUTTON (desktop only) ── */
        /* ── FLOATING BOOK BUTTON ── */
        .ky-float-book{
          display:inline-flex!important;
          transition:opacity 0.3s ease, transform 0.3s ease;
        }
        .ky-float-book.hidden{
          opacity:0!important;
          pointer-events:none!important;
          transform:translateY(8px)!important;
        }
        /* Hide on mobile — sticky bar handles it */
        @media(max-width:820px){.ky-float-book{display:none!important}}
        /* On desktop, ensure it never overlaps inline booking buttons */
        @media(min-width:821px){
          .ky-float-book{bottom:32px!important;right:28px!important}
        }

        /* ── SECTION RHYTHM — consistent spacing + tone ── */
        .ky-section-gap{margin-top:8px}
        @media(max-width:767px){
          .ky-section-gap{margin-top:0}
        }

        /* ── OUR STORY — mobile stack ── */
        @media(max-width:767px){
          .ky-story-hero-grid{
            grid-template-columns:1fr!important;
          }
          .ky-story-image{
            min-height:280px!important;
            max-height:360px;
            overflow:hidden;
          }
          .ky-story-image img{
            height:100%!important;
            object-position:center top;
          }
          .ky-story-text{
            padding:24px 20px!important;
          }
          .ky-story-text h2{
            font-size:clamp(22px,6vw,30px)!important;
            line-height:1.15!important;
            margin-bottom:12px!important;
          }
          .ky-story-text p{
            font-size:15px!important;
            line-height:1.8!important;
          }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, backdropFilter: scrolled ? "blur(12px)" : "none", background: scrolled ? "rgba(0,0,0,.88)" : "transparent", borderBottom: scrolled ? `1px solid ${c.goldDark}33` : "none", transition: "all .3s ease" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 20px", minHeight: 74, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18 }}>
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={{ display: "flex", alignItems: "center", gap: 12, border: 0, background: "transparent", cursor: "pointer", color: c.text, fontFamily: "inherit", padding: 0 }}>
            <img src={logo} alt="KY Empowered Fitness logo" style={{ width: 46, height: 46, objectFit: "contain" }} />
            <div style={{ textAlign: "left" }}>
              <div style={{ color: c.gold, fontSize: 11, letterSpacing: 3, textTransform: "uppercase" }}>KY Empowered Fitness</div>
              <div style={{ color: c.muted, fontSize: 11 }}>Coaches Kevin & Yuneisy</div>
            </div>
          </button>

          <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 22 }}>
            {navLinks.map((link) => (
              <button key={link.id} onClick={() => scrollTo(link.id)} style={{ border: 0, background: "transparent", color: c.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 11, letterSpacing: 2, textTransform: "uppercase" }}>
                {link.label}
              </button>
            ))}
            <LangToggle lang={lang} setLang={setLangPersisted} />
            <button onClick={openCalendly} style={{ ...primaryBtn, padding: "12px 18px", fontSize: 11, background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})` }}>
              {lang === "es" ? "Reservar Llamada" : "Book Free Call"}
            </button>
          </div>

          <button className="mobile-toggle" onClick={() => setMobileMenuOpen((v) => !v)} aria-label="Open menu" style={{ display: "none", border: `1px solid ${c.border}`, background: "transparent", color: c.gold, width: 42, height: 42, borderRadius: 8, alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 18 }}>
            ☰
          </button>
        </div>

        {mobileMenuOpen ? (
          <div className="mobile-panel" style={{ borderTop: `1px solid ${c.border}`, background: "rgba(6,6,5,.97)", padding: 16 }}>
            <div style={{ display: "grid", gap: 10 }}>
              {navLinks.map((link) => (
                <button key={link.id} onClick={() => scrollTo(link.id)} className="ky-nav-item" style={{ border: `1px solid ${c.border}`, background: c.card, color: c.text, borderRadius: 8, padding: "12px 14px", textAlign: "left", fontFamily: "inherit", letterSpacing: 1, cursor: "pointer" }}>
                  {link.label}
                </button>
              ))}
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <LangToggle lang={lang} setLang={setLangPersisted} />
              </div>
              <div style={{ borderTop: `1px solid ${c.goldDark}44`, marginTop: 4, paddingTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
                <button onClick={openCalendly} style={{ ...primaryBtn, padding: "16px 16px", background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`, boxShadow: `0 0 20px ${c.goldDark}44`, fontSize: 12, letterSpacing: 2 }} className="ky-nav-apply">
                  {lang === "es" ? "Reserva Tu Llamada Gratuita" : "Book Your Free Call"}
                </button>
                <p style={{ color: c.muted, fontSize: 11, textAlign: "center", margin: 0, letterSpacing: 0.5 }}>Takes less than 2 minutes</p>
              </div>
              <button onClick={() => { openIntake(); setMobileMenuOpen(false); }} style={{ background: "transparent", border: `1px solid ${c.border}`, borderRadius: 8, color: c.muted, padding: "12px 16px", cursor: "pointer", fontFamily: "inherit", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", width: "100%", textAlign: "center" }}>
                {lang === "es" ? "Completar Intake" : "Complete Full Intake"}
              </button>
            </div>
          </div>
        ) : null}
      </nav>

      {/* ── HERO ── */}
      <section ref={heroRef} className="ky-hero" style={{ padding: "120px 20px 80px", minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 32%, rgba(139,104,32,.22), transparent 45%)", pointerEvents: "none" }} />
        <div className="hero-grid" style={{ maxWidth: 1160, margin: "0 auto", width: "100%", display: "grid", gridTemplateColumns: "1.15fr .85fr", gap: 40, alignItems: "center" }}>
          <div className="hero-copy" style={{ position: "relative", zIndex: 1 }}>
            <p className="ky-hero-eyebrow" style={{ color: c.gold, fontSize: 11, letterSpacing: 6, textTransform: "uppercase", marginBottom: 16, animation: "fadeUp 0.6s ease both" }}>
              {h.eyebrow}
            </p>
            <h1 className="ky-hero-headline" style={{ fontSize: "clamp(38px,7vw,72px)", lineHeight: 1.02, margin: "0 0 18px", color: c.text, letterSpacing: 0.5, maxWidth: 760, animation: "fadeUp 0.7s ease both" }}>
              {h.headline}
            </h1>
            <p className="ky-hero-sub" style={{ color: c.soft, fontSize: "clamp(16px,2vw,20px)", lineHeight: 1.8, maxWidth: 640, margin: "0 0 28px", animation: "fadeUp 0.8s ease both" }}>
              {h.sub}
            </p>
            <div className="hero-actions" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 8, animation: "fadeUp 0.9s ease both" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <button onClick={openStart} style={{ ...primaryBtn, animation: "glowPulse 3s ease-in-out infinite" }}>
                  {h.ctaStart}
                </button>
                <span style={{ color: c.muted, fontSize: 11, letterSpacing: 0.5 }}>{lang === "es" ? "Califica primero • Gratis" : "Qualify first • Free"}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <button onClick={openCalendly} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "16px 28px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", animation: "glowPulse 4s ease-in-out infinite" }}>
                  {h.ctaBook}
                </button>
                <span style={{ color: c.muted, fontSize: 11, letterSpacing: 0.5 }}>{lang === "es" ? "30 min • Sin compromiso" : "30 min • No commitment"}</span>
              </div>
            </div>
            <p className="ky-hero-microcopy" style={{ color: c.muted, fontSize: 12, marginBottom: 16, letterSpacing: 0.5 }}>{h.ctaMicrocopy}</p>

            <div className="stats-row ky-hero-stats" style={{ display: "flex", gap: 26, flexWrap: "wrap", animation: "fadeUp 1s ease both" }}>
              {h.stats.map((item) => (
                <div key={item.label}>
                  <div className="ky-hero-statval" style={{ color: c.gold, fontSize: 30, fontWeight: 700 }}>{item.value}</div>
                  <div style={{ color: c.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginTop: 4 }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="ky-hero-card" style={{ position: "relative", zIndex: 1 }}>
            <Card>
              <img src={logo} alt="KY Empowered Fitness" style={{ width: "100%", maxWidth: 320, display: "block", margin: "0 auto 18px", objectFit: "contain" }} />
              <div style={{ borderTop: `1px solid ${c.border}`, paddingTop: 18, display: "grid", gap: 14 }}>
                {h.bullets.map((line) => (
                  <div key={line} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ color: c.gold }}>✦</span>
                    <span style={{ color: c.soft, lineHeight: 1.8, fontSize: 15 }}>{line}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button onClick={openStart} style={{ flex: 1, minWidth: 120, ...primaryBtn, padding: "13px 14px", fontSize: 11 }}>{h.ctaStart}</button>
                <button onClick={openCalendly} style={{ flex: 1, minWidth: 120, background: "transparent", border: `1px solid ${c.goldDark}66`, borderRadius: 8, color: c.gold, padding: "13px 14px", cursor: "pointer", fontFamily: "inherit", fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>{h.ctaBook}</button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* ── NBC TRUST BAR ── */}
      <a href="https://www.nbcmiami.com/" target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none" }}>
        <div className="ky-trust-bar" style={{ background: "#0a0a08", borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}`, padding: "14px 20px", textAlign: "center" }}>
          <p style={{ color: c.muted, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", margin: 0 }}>
            {t.trustBar.label}&nbsp;&nbsp;<span style={{ color: c.gold, fontWeight: 700, letterSpacing: 1 }}>{t.trustBar.nbc}</span>&nbsp;<span style={{ color: c.muted, fontSize: 10 }}>↗</span>
          </p>
        </div>
      </a>

      {/* ── RESULTS ── */}
      <ResultsSection t={t} onStart={openStart} onTalk={openCalendly} />

      {/* ── TESTIMONIALS ── */}
      <TestimonialsSection t={t} onTestimonial={openTestimonials} onStart={openStart} onTalk={openCalendly} />

      {/* ── ABOUT ── */}
      <section id="about" className="ky-section" style={{ maxWidth: 1120, margin: "0 auto", padding: "90px 20px" }}>
        <Divider />
        <SectionTitle label={t.about.label} title={t.about.title} subtitle={t.about.subtitle} />
        <div className="ky-about-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 22 }}>
          {t.about.cards.map((item) => (
            <Card className="ky-about-card" key={item.title}>
              <h3 style={{ color: c.gold, fontSize: 24, margin: "0 0 10px" }}>{item.title}</h3>
              <p style={{ color: c.muted, lineHeight: 1.85, fontSize: 15, margin: 0 }}>{item.desc}</p>
            </Card>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <button onClick={() => navigate("/about-us")} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "14px 24px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" }}>
            {t.about.readStory}
          </button>
        </div>
      </section>

      {/* ── HOW WE COACH ── */}
      <HowWeCoachSection t={t} onApply={openCalendly} />

      {/* ── NEW: KY TRANSFORMATION SYSTEM ── */}
      <TransformationSystemSection t={t} onStart={openStart} />

      {/* ── HOW WE DELIVER ── */}
      <HowWeDeliverSection t={t} onStart={openCalendly} />

      {/* ── WHO THIS IS FOR ── */}
      <WhoThisIsFor t={t} onApply={openCalendly} />

      {/* ── SERVICES ── */}
      <section id="services" className="ky-section" style={{ maxWidth: 1120, margin: "0 auto", padding: "90px 20px" }}>
        <Divider />
        <p style={{ textAlign: "center", color: c.gold, fontSize: 11, letterSpacing: 4, textTransform: "uppercase", marginBottom: 14, opacity: 0.85 }}>{t.services.microHeader}</p>
        <SectionTitle label={t.services.label} title={t.services.title} subtitle={t.services.subtitle} />
        <div className="ky-services-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20 }}>
          {t.services.items.map((svc) => (
            <Card className="ky-service-card" key={svc.title}>
              <div style={{ fontSize: 30, marginBottom: 10 }}>{svc.icon}</div>
              <h3 style={{ color: c.gold, fontSize: 23, margin: "0 0 10px" }}>{svc.title}</h3>
              <p style={{ color: c.muted, lineHeight: 1.8, margin: "0 0 18px", fontSize: 15 }}>{svc.desc}</p>
              <button onClick={openCalendly} style={{ background: "transparent", border: `1px solid ${c.goldDark}66`, borderRadius: 8, color: c.gold, padding: "10px 16px", cursor: "pointer", fontFamily: "inherit", letterSpacing: 1 }}>
                {t.services.ask}
              </button>
            </Card>
          ))}
        </div>
      </section>

      {/* ── CREDENTIALS ── */}
      <CredentialsSection t={t} onApply={openCalendly} />

      {/* ── MID CTA ── */}
      <section style={{ padding: "0 20px 90px" }}>
        <div className="ky-cta-block" style={{ maxWidth: 1120, margin: "0 auto", background: "linear-gradient(135deg, rgba(12,12,7,1), rgba(20,18,10,1), rgba(12,12,7,1))", border: `1px solid ${c.goldDark}44`, borderRadius: 20, padding: "42px 28px", textAlign: "center" }}>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 12 }}>{t.midCta.eyebrow}</p>
          <h2 style={{ color: c.text, fontSize: "clamp(30px,5vw,48px)", margin: "0 0 14px" }}>{t.midCta.title}</h2>
          <p style={{ color: c.soft, lineHeight: 1.8, maxWidth: 760, margin: "0 auto 24px", fontSize: 16 }}>{t.midCta.sub}</p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={openStart} style={primaryBtn}>{lang === "es" ? "Comienza Tu Transformación" : "Start Your Transformation"}</button>
            <button onClick={openCalendly} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "16px 28px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" }}>{lang === "es" ? "Reserva Tu Llamada" : "Book Your Free Call"}</button>
          </div>
        </div>
      </section>

      {/* ── INVESTMENT / COACHING PATHS (reframed) ── */}
      <InvestmentSection t={t} onApply={openCalendly} />

      {/* ── REFERRAL ── */}
      <section style={{ padding: "0 20px 60px" }}>
        <div className="ky-cta-block" style={{ maxWidth: 1120, margin: "0 auto", background: "linear-gradient(135deg, rgba(12,12,7,1), rgba(20,18,10,1), rgba(12,12,7,1))", border: `1px solid ${c.goldDark}44`, borderRadius: 20, padding: "42px 28px", textAlign: "center" }}>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 12 }}>{t.referral.eyebrow}</p>
          <h2 style={{ color: c.text, fontSize: "clamp(28px,5vw,42px)", margin: "0 0 14px" }}>{t.referral.title}</h2>
          <p style={{ color: c.soft, lineHeight: 1.8, maxWidth: 600, margin: "0 auto 24px", fontSize: 15 }}>{t.referral.sub}</p>
          <div className="ky-referral-btns" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/referral")} style={primaryBtn}>{t.referral.cta}</button>
            <button onClick={() => navigate("/referral")} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "16px 28px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" }}>
              {t.referral.learnMore}
            </button>
          </div>
        </div>
      </section>

      {/* ── BOOKING SECTION ── */}
      <section style={{ padding: "0 20px 80px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", background: "linear-gradient(160deg, #0c0b07, #13110a, #0c0b07)", border: `1px solid ${c.gold}55`, borderRadius: 20, padding: "52px 36px", textAlign: "center", boxShadow: `0 0 60px ${c.goldDark}18` }}>
          <p style={{ color: c.gold, fontSize: 10, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14 }}>{t.booking.label}</p>
          <h2 style={{ color: c.text, fontSize: "clamp(26px,5vw,42px)", lineHeight: 1.1, margin: "0 0 16px" }}>{t.booking.title}</h2>
          <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, maxWidth: 560, margin: "0 auto 32px" }}>{t.booking.sub}</p>
          <button
            onClick={openCalendly}
            style={{ ...primaryBtn, fontSize: 14, padding: "18px 40px", marginBottom: 16, minHeight: 56, animation: "glowPulse 3s ease-in-out infinite" }}
          >
            {t.booking.cta}
          </button>
          <p style={{ color: c.muted, fontSize: 12, letterSpacing: 0.5, margin: 0 }}>❆ {t.booking.trust}</p>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" style={{ maxWidth: 860, margin: "0 auto", padding: "0 20px 110px", textAlign: "center" }}>
        <Divider />
        <SectionTitle label={t.contact.label} title={t.contact.title} subtitle={t.contact.subtitle} />
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <button onClick={openCalendly} style={{ ...primaryBtn, minHeight: 48 }}>{lang === "es" ? "Reserva Tu Llamada Gratuita" : "Book Your Free Call"}</button>
          <button onClick={openStart} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "14px 22px", letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", minHeight: 48 }}>{lang === "es" ? "Comienza Tu Transformación" : "Start Your Transformation"}</button>
        </div>
        <p style={{ color: c.muted, fontSize: 12, textAlign: "center", marginBottom: 28, letterSpacing: 0.5 }}>❆ {lang === "es" ? "Lugares limitados cada semana para garantizar un coaching de calidad." : "Limited spots available each week to ensure quality coaching."}</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
          <a href="https://instagram.com/kyempoweredfitness" target="_blank" rel="noreferrer" style={{ textDecoration: "none", background: c.card, border: `1px solid ${c.goldDark}44`, borderRadius: 12, padding: "16px 18px", textAlign: "left" }}>
            <div style={{ color: c.muted, fontSize: 9, letterSpacing: 3, textTransform: "uppercase", marginBottom: 5 }}>Instagram</div>
            <div style={{ color: c.gold, fontSize: 15, fontWeight: 700 }}>@kyempoweredfitness</div>
            <div style={{ color: c.muted, fontSize: 11, marginTop: 3 }}>{t.contact.dmSub}</div>
          </a>
          <a href={"tel:" + t.contact.phoneNumber.replace(/ /g, "")} style={{ textDecoration: "none", background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: "16px 18px", textAlign: "left" }}>
            <div style={{ color: c.muted, fontSize: 9, letterSpacing: 3, textTransform: "uppercase", marginBottom: 5 }}>{t.contact.phoneLabel}</div>
            <div style={{ color: c.gold, fontSize: 15, fontWeight: 700 }}>{t.contact.phoneNumber}</div>
            <div style={{ color: c.muted, fontSize: 11, marginTop: 3 }}>{t.contact.phoneSub}</div>
          </a>
          <a href={t.contact.googleUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none", background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: "16px 18px", textAlign: "left" }}>
            <div style={{ color: c.muted, fontSize: 9, letterSpacing: 3, textTransform: "uppercase", marginBottom: 5 }}>{t.contact.googleLabel}</div>
            <div style={{ color: c.gold, fontSize: 15, fontWeight: 700 }}>⭐ 5.0 Google</div>
            <div style={{ color: c.muted, fontSize: 11, marginTop: 3 }}>{t.contact.googleSub}</div>
          </a>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop: `1px solid ${c.border}`, padding: "36px 20px 46px", textAlign: "center" }}>
        <img src={logo} alt="KY logo" style={{ width: 60, height: 60, objectFit: "contain", opacity: 0.75, marginBottom: 12 }} />
        <div style={{ color: c.muted, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>
          Kevin Fajardo & Yuneisy Sarante
        </div>
        <div style={{ color: "#64593d", fontSize: 11 }}>© 2026 KY Empowered Fitness • Miami, Florida</div>
        <div className="ky-footer-links" style={{ marginTop: 16, display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={openStart} style={{ background: "transparent", border: "none", color: c.muted, cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: "inherit" }}>{lang === "es" ? "Comienza Tu Transformación" : "Start Your Transformation"}</button>
          <button onClick={openCalendly} style={{ background: "transparent", border: "none", color: c.muted, cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: "inherit" }}>{lang === "es" ? "Reserva Tu Llamada" : "Book Your Free Call"}</button>
          <button onClick={() => navigate("/checkin")} style={{ background: "transparent", border: "none", color: c.muted, cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: "inherit" }}>{lang === "es" ? "Check-In" : "Client Check-In"}</button>
          <button onClick={() => setShowTestimonialsModal(true)} style={{ background: "transparent", border: "none", color: c.muted, cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: "inherit" }}>{lang === "es" ? "Dejar Reseña" : "Leave a Review"}</button>
        </div>
      </footer>

      {/* ── MOBILE STICKY CTA ── */}
      {showScrollCTA ? (
        <div className="mobile-toggle ky-sticky-cta" style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200, padding: "12px 16px", paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))", background: "rgba(8,8,6,.97)", borderTop: `1px solid ${c.goldDark}55`, boxShadow: "0 -6px 24px rgba(0,0,0,0.65)", display: "flex", gap: 10 }}>
          <button onClick={openStart} style={{ ...primaryBtn, flex: 1, padding: "14px 12px", fontSize: 12 }}>{h.ctaStart}</button>
          <button onClick={openCalendly} style={{ background: "transparent", color: c.gold, border: `1px solid ${c.goldDark}`, borderRadius: 8, padding: "14px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", flex: 1 }}>{h.ctaBook}</button>
        </div>
      ) : null}

      {/* ── FLOATING BOOK BUTTON (desktop only, hides near bottom) ── */}
      <button
        onClick={openCalendly}
        className={`ky-float-book${showFloat ? "" : " hidden"}`}
        aria-hidden={!showFloat}
        style={{
          position: "fixed", bottom: 32, right: 28, zIndex: 150,
          background: `linear-gradient(135deg, ${c.goldDark}, ${c.gold})`,
          color: c.black, border: "none", borderRadius: 999,
          padding: "13px 22px", fontWeight: 900, letterSpacing: 2,
          textTransform: "uppercase", cursor: "pointer",
          fontFamily: "inherit", fontSize: 11,
          boxShadow: `0 8px 30px ${c.goldDark}55`,
          opacity: showFloat ? 1 : 0,
          pointerEvents: showFloat ? "auto" : "none",
          transition: "opacity 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 40px ${c.goldDark}77`; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 8px 30px ${c.goldDark}55`; }}
      >
        📞 {lang === "es" ? "Reservar" : "Book a Call"}
      </button>

      {/* ── MODALS ── */}
      {showApplyModal ? <ApplyModal onClose={() => setShowApplyModal(false)} t={t} /> : null}
      {showTestimonialsModal ? <TestimonialsModal onClose={() => setShowTestimonialsModal(false)} t={t} /> : null}
    </div>
  );
}
